import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Copy, Loader2, Plus, Trash2, Undo2, Search,
  ClipboardPaste, PackageCheck, PauseCircle, PlayCircle, CheckCircle2,
  XCircle, ChevronRight, FileText, Ban, Star, RotateCcw, Printer, MessageCircle,
  Save, Archive as ArchiveIcon, AlertTriangle, AlertCircle, Minus, Filter, Mail, Send, X,
  MapPin, Truck, ClipboardCheck, DollarSign, PackageX,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { queueTwoLine, queueCompact, detailLong, DATE_UNAVAILABLE } from "@/lib/businessTime";
import PickingListModal from "@/components/admin/PickingListModal";
import VariantBadge from "@/components/admin/VariantBadge";
import { Link } from "react-router-dom";
import { RecordPickupModal, FulfillmentHistory, useFulfillments } from "@/components/admin/RecordPickup";
import { RefundModal, RefundAndCancelModal } from "@/components/admin/RefundModal";
import { RefundHistoryCard } from "@/components/admin/RefundHistoryCard";
import NotificationHistoryCard from "@/components/admin/NotificationHistoryCard";
import ZellePaymentVerificationCard from "@/components/admin/ZellePaymentVerificationCard";
import {
  getEffectivePaymentMethod, isPaymentVerified,
  shouldShowZelleReconciliation, METHOD_LABELS,
} from "@/lib/paymentMethod";
import {
  FULFILLMENT_SORT_OPTIONS, safeFulfillmentSort,
} from "@/lib/fulfillmentSort";
import AdminChangeDeliveryModal from "@/components/admin/AdminChangeDeliveryModal";
import { albumSortKey, parseCode } from "@/lib/albumOrder";

/**
 * FulfillmentAdmin — top-level workspace mounted from AdminDashboard.
 * When a `:public_id` route param is present we render the single-order
 * workspace; otherwise we render the queue.
 *
 * Filters/sort/scroll are preserved across queue↔detail navigation via
 * sessionStorage (per user spec).
 */
const QUEUE_STATE_KEY = "gm.fulfillment.queue.v1";

// P3.2 · Canonical variant vocabulary + customer-friendly labels
// used by every fulfillment surface (header badge, picking chip).
const FULFILL_VARIANT_LABELS = {
  white:        "White",
  blue:         "Blue Parallel",
  gold_crumple: "Gold Crumple",
  red:          "Red Parallel",
  orange:       "Orange Parallel",
  purple:       "Purple Parallel",
};
function OrderVariantBadge({ variant }) {
  const v = String(variant || "white").toLowerCase();
  if (v === "white") return null;
  const tone = {
    blue:         "bg-[#2C6EF2] text-white",
    gold_crumple: "bg-amber-500 text-black",
    red:          "bg-red-600 text-white",
    orange:       "bg-orange-500 text-white",
    purple:       "bg-purple-600 text-white",
  }[v] || "bg-neutral-800 text-white";
  return (
    <span
      className={"inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest border-2 border-[var(--ink)] " + tone}
      data-testid={`order-variant-badge-${v}`}
      title={`Customer requested ${FULFILL_VARIANT_LABELS[v]} stickers`}
    >
      {FULFILL_VARIANT_LABELS[v]}
    </span>
  );
}
function PickingVariantChip({ variant }) {
  const v = String(variant || "white").toLowerCase();
  // Feb-2026 · WHITE must be as obvious as BLUE / GOLD; never hidden.
  const tone = {
    white:        "bg-white text-black border-black",
    blue:         "bg-blue-50 text-blue-800 border-blue-300",
    gold_crumple: "bg-amber-50 text-amber-800 border-amber-300",
    red:          "bg-red-50 text-red-800 border-red-300",
    orange:       "bg-orange-50 text-orange-800 border-orange-300",
    purple:       "bg-purple-50 text-purple-800 border-purple-300",
  }[v] || "bg-neutral-50 text-neutral-800 border-neutral-300";
  return (
    <span
      className={"inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border " + tone}
      data-testid={`picking-variant-chip-${v}`}
      title={FULFILL_VARIANT_LABELS[v] || v}
    >
      {FULFILL_VARIANT_LABELS[v] || v}
    </span>
  );
}



const PRIORITIES = {
  vip: { label: "VIP", tone: "red", rank: 4 },
  event_pickup: { label: "Event pickup", tone: "yellow", rank: 3 },
  rush: { label: "Rush", tone: "red", rank: 2 },
  normal: { label: "Normal", tone: "gray", rank: 1 },
};

const STATUSES = ["awaiting_payment", "payment_submitted", "payment_verified", "preparing", "ready", "completed", "cancelled"];
const STATUS_LABELS = {
  awaiting_payment: "Awaiting payment",
  payment_submitted: "Payment sent · needs verify",
  payment_verified: "Payment verified",
  preparing: "Preparing",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function FulfillmentAdmin() {
  const { public_id } = useParams();
  return public_id ? <FulfillmentWorkspace publicId={public_id} /> : <FulfillmentQueue />;
}

// ─────────────────────────────────────────────────────────────────────
//  Queue
// ─────────────────────────────────────────────────────────────────────
function FulfillmentQueue() {
  const nav = useNavigate();
  const scrollRef = useRef(null);
  const persisted = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(QUEUE_STATE_KEY) || "{}"); } catch { return {}; }
  }, []);
  const [statusFilter, setStatusFilter] = useState(persisted.statusFilter || "all");
  const [fulfillmentGroup, setFulfillmentGroup] = useState(persisted.fulfillmentGroup || "all");
  const [creationFilter, setCreationFilter] = useState(persisted.creationFilter || "all");
  const [search, setSearch] = useState(persisted.search || "");
  const [priorityFilter, setPriorityFilter] = useState(persisted.priorityFilter || "all");
  const [rows, setRows] = useState(null);
  const [activeSessions, setActiveSessions] = useState(new Set());
  const [loading, setLoading] = useState(false);
  // Phase 1A · Sort By — session-remembered from persisted queue state
  // via `persisted.sortBy` further below. Default is `newest`.
  const [queueSort, setQueueSort] = useState(safeFulfillmentSort(persisted.sortBy));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (creationFilter !== "all") params.creation_method = creationFilter;
      if (statusFilter === "ready" && (fulfillmentGroup === "pickup" || fulfillmentGroup === "ship" || fulfillmentGroup === "unknown")) {
        params.fulfillment_group = fulfillmentGroup;
      }
      params.limit = 200;
      params.sort = queueSort || "newest";
      const [r, sess] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/orders-sessions/active"),
      ]);
      setRows(r.data.orders || []);
      setActiveSessions(new Set((sess.data.sessions || []).map((s) => s.order_public_id)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load orders.");
    } finally { setLoading(false); }
  }, [statusFilter, creationFilter, fulfillmentGroup, queueSort]);

  useEffect(() => { load(); }, [load]);

  // Persist filters + sort + scroll
  useEffect(() => {
    sessionStorage.setItem(QUEUE_STATE_KEY, JSON.stringify({ statusFilter, fulfillmentGroup, creationFilter, search, priorityFilter, sortBy: queueSort, scrollY: window.scrollY }));
  }, [statusFilter, fulfillmentGroup, creationFilter, search, priorityFilter, queueSort]);
  useEffect(() => {
    // Phase 1A · Only restore prior scroll for real browser
    // back/forward navigation. Fresh row clicks / dashboard drill-downs
    // must always start at the top of the page.
    let isBackForward = false;
    try {
      const nav = performance.getEntriesByType && performance.getEntriesByType("navigation");
      isBackForward = !!(nav && nav[0] && nav[0].type === "back_forward");
    } catch (_) { /* ignore */ }
    if (isBackForward && persisted.scrollY) {
      requestAnimationFrame(() => window.scrollTo(0, persisted.scrollY));
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (priorityFilter !== "all" && (r.priority || "normal") !== priorityFilter) return false;
      if (!s) return true;
      return (r.public_id || "").toLowerCase().includes(s)
        || (r.customer?.name || "").toLowerCase().includes(s)
        || (r.customer?.email || "").toLowerCase().includes(s);
    });
  }, [rows, search, priorityFilter]);

  return (
    <div data-testid="fulfillment-queue">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl">Fulfillment queue</h2>
          <p className="text-sm text-[var(--smoke)] mt-1">
            {rows == null ? "Loading…" : `${filtered?.length ?? 0} order${(filtered?.length ?? 0) === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 opacity-50" size={16} />
            <input
              type="text"
              placeholder="Search order ID, name, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-2 border-2 border-[var(--ink)] rounded-lg text-sm"
              data-testid="fulfillment-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              const next = e.target.value;
              setStatusFilter(next);
              // Any change off "ready" clears the pickup/ship filter so
              // it can never leak into other queue views.
              if (next !== "ready" && fulfillmentGroup !== "all") setFulfillmentGroup("all");
            }}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
            data-testid="fulfillment-status-filter"
          >
            <option value="all">All statuses</option>
            {["payment_submitted", ...STATUSES].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          {statusFilter === "ready" && (
            <select
              value={fulfillmentGroup}
              onChange={(e) => setFulfillmentGroup(e.target.value)}
              className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
              data-testid="fulfillment-group-filter"
              title="Split the Ready bucket by fulfillment method"
            >
              <option value="all">All ready orders</option>
              <option value="pickup">Ready for Pickup</option>
              <option value="ship">Ready to Ship</option>
              <option value="unknown">Ready — Delivery Review Needed</option>
            </select>
          )}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
            data-testid="fulfillment-priority-filter"
          >
            <option value="all">All priorities</option>
            {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select
            value={creationFilter}
            onChange={(e) => setCreationFilter(e.target.value)}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
            data-testid="fulfillment-creation-filter"
            title="Filter by how the order was created"
          >
            <option value="all">All orders</option>
            <option value="manual">Manual entries</option>
            <option value="website">Website orders</option>
          </select>
          <select
            value={queueSort}
            onChange={(e) => setQueueSort(safeFulfillmentSort(e.target.value))}
            className="border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
            data-testid="fulfillment-sort"
            title="Sort By"
          >
            {FULFILLMENT_SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button onClick={() => setQueueSort("newest")} className="btn-ghost text-xs"
                   data-testid="fulfillment-sort-reset" title="Reset sort to newest">
            Reset sort
          </button>
          <button onClick={load} className="btn-ghost text-sm" data-testid="fulfillment-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Refresh
          </button>
        </div>
      </div>

      <div className="mt-6 border-2 border-[var(--ink)] rounded-2xl bg-white overflow-x-auto" ref={scrollRef}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
              <th className="px-4 py-3 text-left">Priority</th>
              <th className="px-4 py-3 text-left">Order</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">Order Placed</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">Payment</th>
              <th className="px-4 py-3 text-right">Progress</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered === null && (
              <tr><td colSpan={9} className="text-center py-8"><Loader2 className="animate-spin inline" size={20}/></td></tr>
            )}
            {filtered && filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[var(--smoke)]" data-testid="fulfillment-empty">No orders match your filters.</td></tr>
            )}
            {filtered && filtered.map((o) => {
              const p = PRIORITIES[o.priority || "normal"];
              const s = o.fulfillment_summary || {};
              const pct = s.percent_complete ?? 0;
              return (
                <tr
                  key={o.public_id}
                  className="border-b border-[var(--stone)] hover:bg-[var(--panini-yellow-soft)] cursor-pointer"
                  onClick={() => nav(`/admin/fulfillment/${o.public_id}`)}
                  data-testid={`queue-row-${o.public_id}`}
                >
                  <td className="px-4 py-3">
                    <PriorityBadge value={o.priority || "normal"} />
                  </td>
                  <td className="px-4 py-3 font-mono font-bold">
                    <div className="flex items-center gap-2 flex-wrap">
                      {o.public_id}
                      {/* Phase 3A.1 — Pickup/Shipping badge in the queue list. */}
                      <FulfillmentMethodBadge group={o.fulfillment_group} method={o.fulfillment_method} size="sm" />
                      {o.creation_method === "manual" && (
                        <ManualSourceBadge source={o.source} testid={`queue-manual-${o.public_id}`} />
                      )}
                      {activeSessions.has(o.public_id) && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-emerald-900 border border-emerald-900 bg-emerald-100 rounded px-1.5 py-0.5"
                          data-testid={`queue-session-badge-${o.public_id}`}
                          title="Fulfillment session is active"
                        >
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-600 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-700"></span>
                          </span>
                          Active
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Iter S · Stage 1 — Order Placed column (America/New_York).
                      Desktop shows two lines; narrow screens collapse to one. */}
                  <td className="px-4 py-3 text-left whitespace-nowrap" data-testid={`queue-placed-${o.public_id}`}>
                    <OrderPlacedCell iso={o.created_at} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold">{o.customer?.name || "—"}</div>
                    <div className="text-xs opacity-60">{o.customer?.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusChip status={o.status} />
                  </td>
                  {/* Iter S · Stage 2 — Payment badge (Zelle-only). */}
                  <td className="px-4 py-3" data-testid={`queue-payment-${o.public_id}`}>
                    <ZellePaymentBadge order={o} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-xs opacity-60">{s.ready_total || 0}/{s.requested_total || 0}</div>
                    <div className="w-24 h-1.5 bg-neutral-200 rounded-full inline-block mt-1">
                      <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    <div>${(o.pricing_snapshot?.totals?.total || 0).toFixed(2)}</div>
                    {/* Iter S · Stage 2 — Amount received + difference under total. */}
                    <QueueZelleAmount rec={o.payment_reconciliation} testId={o.public_id} />
                  </td>
                  <td className="px-4 py-3 text-right"><ChevronRight size={16} className="opacity-40 inline" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Iter S · Stage 1 — Order Placed cell for the Fulfillment Queue.
 *
 * Desktop (≥md): two lines — date on top, time below — so the column
 * stays narrow and the queue doesn't blow out horizontally.
 * Narrow screens: single-line compact "Jul 30, 2:42 PM".
 * Missing timestamp: "Date unavailable" (never fabricated).
 *
 * Timezone: America/New_York — CollectorUp business time.
 */
/**
 * Iter S · Stage 2 — Payment badge for the Fulfillment Queue.
 *
 * Zelle-only per operational policy. Three at-a-glance states, derived
 * from the reconciliation snapshot + existing order.status. Never
 * automatically marks a payment as matched — an admin must set
 * verification_status="matched" explicitly.
 */
// Phase 1 · Manual payment badge — mirrors payment.method verbatim so the
// admin sees exactly how the order was paid (Cash / Card / Zelle / Cash
// App / Pay In Store). Zero data duplication; reads directly from
// `order.payment.method` and falls back to legacy manual orders.
const PAID_VIA_LABELS = {
  cash: "Cash",
  card: "Card",
  zelle: "Zelle",
  cashapp: "Cash App",
  pay_in_store: "Pay In Store",
  manual: "Manual",
};

function PaidViaBadge({ order }) {
  const method = String(order?.payment?.method || "").toLowerCase();
  const isManual = order?.creation_method === "manual" || order?.manual_entry === true;
  // Awaiting-payment manual orders have no payment envelope yet — show
  // "Awaiting via: Pay In Store" so the admin still sees the intended
  // channel next to the pickup / shipping badges.
  const isAwaiting = order?.status === "awaiting_payment";
  const derived = !method && isManual && isAwaiting ? "pay_in_store" : method;
  if (!derived) return null;
  if (!isManual && !["cash", "card", "cashapp", "pay_in_store"].includes(derived)) return null;

  const label = PAID_VIA_LABELS[derived] || derived;
  const tone = isAwaiting
    ? "border-amber-800 bg-amber-50 text-amber-900"
    : "border-emerald-800 bg-emerald-50 text-emerald-900";
  const prefix = isAwaiting ? "Awaiting via:" : "Paid via:";
  return (
    <span
      className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${tone}`}
      data-testid={`paid-via-badge-${derived}`}
      title={`${prefix} ${label}`}
    >
      {prefix} {label}
    </span>
  );
}

function ZellePaymentBadge({ order }) {
  // Phase 1-ops · Prefer the server-computed payment_display resolver
  // so queue + detail cannot disagree. Legacy client-side fallback
  // kept below for older API responses that predate the resolver.
  const pd = order?.payment_display;
  if (pd?.label) {
    const toneCls = pd.tone === "ok"
      ? "border-emerald-800 bg-emerald-50 text-emerald-900"
      : pd.tone === "warn"
        ? "border-amber-800 bg-amber-50 text-amber-900"
        : "border-neutral-400 bg-neutral-50 text-neutral-700";
    return (
      <span
        className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${toneCls}`}
        data-testid={`zelle-payment-badge-${pd.tone}`}
        data-payment-state={pd.state}
      >
        {pd.label}
      </span>
    );
  }
  const rec = order?.payment_reconciliation || {};
  const vstatus = rec.zelle_verification_status;
  const orderStatus = order?.status;
  let label, tone;
  if (vstatus === "matched") {
    label = "Zelle Payment Matched";
    tone = "ok";
  } else if (vstatus && vstatus !== "not_reviewed") {
    // amount_differs | sender_name_differs | needs_customer_confirmation
    label = "Payment Needs Review";
    tone = "warn";
  } else if (orderStatus === "payment_submitted") {
    label = "Payment Needs Review";
    tone = "warn";
  } else if (orderStatus === "completed" || orderStatus === "shipped" ||
             orderStatus === "refunded" || orderStatus === "partially_refunded") {
    // Phase 1 order-truth: terminal orders are by definition paid.
    // Previous fallback incorrectly treated them as "Awaiting Zelle
    // Payment" when verified_at wasn't written by the legacy flow.
    label = "Payment Verified";
    tone = "ok";
  } else if (orderStatus === "awaiting_payment") {
    label = "Awaiting Zelle Payment";
    tone = "mute";
  } else {
    // payment_verified / preparing / ready with no reconciliation
    // recorded yet — still "Awaiting Zelle Payment" from a reconciliation
    // standpoint even if payment was verified through the legacy flow.
    // Show "Awaiting" only when no amount has been recorded; otherwise
    // "Needs Review".
    if (rec.zelle_amount_received !== null && rec.zelle_amount_received !== undefined) {
      label = "Payment Needs Review";
      tone = "warn";
    } else {
      label = "Awaiting Zelle Payment";
      tone = "mute";
    }
  }
  const toneCls = tone === "ok"
    ? "border-emerald-800 bg-emerald-50 text-emerald-900"
    : tone === "warn"
      ? "border-amber-800 bg-amber-50 text-amber-900"
      : "border-neutral-400 bg-neutral-50 text-neutral-700";
  return (
    <span
      className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${toneCls}`}
      data-testid={`zelle-payment-badge-${tone}`}
    >
      {label}
    </span>
  );
}


/**
 * Iter S · Stage 2 — Amount-received + difference display under the Total.
 * Renders "Zelle received: —" when no payment amount has been recorded.
 */
function QueueZelleAmount({ rec, testId }) {
  const amt = rec?.zelle_amount_received;
  const diff = rec?.difference;
  const hasAmt = amt !== null && amt !== undefined && amt !== "";
  const diffLabel = diff === null || diff === undefined
    ? ""
    : diff === 0
      ? "$0.00"
      : `${diff > 0 ? "+" : "-"}$${Math.abs(diff).toFixed(2)}`;
  const diffTone = diff === null || diff === undefined || diff === 0
    ? "text-[var(--smoke)]"
    : "text-amber-800";
  return (
    <div className="text-[10px] leading-tight mt-1" data-testid={`queue-zelle-amount-${testId}`}>
      <div className="opacity-60">
        Zelle received:{" "}
        <span className="font-mono">
          {hasAmt ? `$${Number(amt).toFixed(2)}` : "—"}
        </span>
      </div>
      {hasAmt && diffLabel && (
        <div className={diffTone}>
          Diff: <span className="font-mono">{diffLabel}</span>
        </div>
      )}
    </div>
  );
}


function OrderPlacedCell({ iso }) {
  const two = queueTwoLine(iso);
  const compact = queueCompact(iso);
  if (!two || !compact) {
    return (
      <span className="text-xs italic text-[var(--smoke)]" data-testid="queue-placed-missing">
        {DATE_UNAVAILABLE}
      </span>
    );
  }
  return (
    <>
      <div className="hidden md:block text-xs leading-tight">
        <div className="font-semibold">{two.date}</div>
        <div className="opacity-70">{two.time}</div>
      </div>
      <div className="md:hidden text-xs">{compact}</div>
    </>
  );
}

/**
 * Iter S · Stage 1 — Order Information block on the admin detail page.
 * Placed inside the order-header card, immediately below the address block,
 * so operators see when the order was created without scrolling.
 *
 * `Order placed` is heavier weight than `Last updated`, per spec.
 */
function OrderInformationSection({ order }) {
  const placed = detailLong(order.created_at);
  const updated = detailLong(order.updated_at);
  const showUpdated = updated && updated !== placed;
  return (
    <div
      className="mt-4 border-l-4 border-[var(--ink)] pl-3 py-2 bg-[var(--panini-yellow-soft)]/30 rounded-r"
      data-testid="order-information"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)]">
        Order Information
      </div>
      <div className="mt-1 flex flex-col gap-0.5">
        <div className="text-sm md:text-base">
          <span className="font-bold text-[var(--ink)]">Order placed:</span>{" "}
          <span data-testid="detail-order-placed">
            {placed || <span className="italic text-[var(--smoke)]">{DATE_UNAVAILABLE}</span>}
          </span>
        </div>
        {showUpdated && (
          <div className="text-xs opacity-70">
            <span className="font-semibold">Last updated:</span>{" "}
            <span data-testid="detail-order-updated">{updated}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Iter S · Stage 1 — One-click copy of the customer email address.
 * Uses the async Clipboard API with a legacy execCommand fallback so it
 * works in old preview browsers and non-secure contexts.
 */
function CopyEmailButton({ email }) {
  const [copied, setCopied] = useState(false);
  const doCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement("textarea");
        ta.value = email;
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Email copied");
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error("Could not copy — please copy manually");
    }
  }, [email]);
  return (
    <button
      type="button"
      onClick={doCopy}
      title={copied ? "Copied!" : "Copy email address"}
      aria-label="Copy email address"
      data-testid="copy-customer-email"
      className={`shrink-0 inline-flex items-center gap-1 border-2 border-[var(--ink)] rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition ${
        copied ? "bg-emerald-100 text-emerald-900" : "bg-white hover:bg-[var(--panini-yellow)]"
      }`}
    >
      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function PriorityBadge({ value }) {
  const p = PRIORITIES[value] || PRIORITIES.normal;
  const tone = {
    red: "bg-red-100 border-red-900 text-red-900",
    yellow: "bg-yellow-100 border-yellow-900 text-yellow-900",
    gray: "bg-neutral-100 border-neutral-500 text-neutral-800",
  }[p.tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold border rounded ${tone}`}>
      {p.tone === "red" && <Star size={10} />}
      {p.label}
    </span>
  );
}

function StatusChip({ status }) {
  const label = STATUS_LABELS[status] || status;
  const tone = {
    payment_submitted: "bg-blue-100 border-blue-900 text-blue-900",
    payment_verified:  "bg-emerald-100 border-emerald-900 text-emerald-900",
    preparing:         "bg-yellow-100 border-yellow-900 text-yellow-900",
    ready:             "bg-emerald-100 border-emerald-900 text-emerald-900",
    completed:         "bg-neutral-100 border-neutral-800 text-neutral-800",
    cancelled:         "bg-red-100 border-red-900 text-red-900",
  }[status] || "bg-neutral-100 border-neutral-500 text-neutral-800";
  return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase font-bold border rounded ${tone}`}>{label}</span>;
}

/**
 * Owner P0 · Shipping-workflow repair (scenario 2).
 *
 * Renders a derived "SHIPPED" / "PARTIALLY SHIPPED" pill next to
 * the persisted-status chip so the owner never has to infer shipping
 * from a generic "Completed" label. Pure derivation — the persisted
 * `sticker_orders.status` and the PF state machine are unaffected.
 *
 * Rules:
 *   • Only shipping orders (delivery.code !== "local_pickup").
 *   • "SHIPPED" when the order is terminally shipped:
 *       (persisted `shipped`) OR
 *       (persisted `completed` AND delivery is shipping) OR
 *       (legacy `order.shipment.tracking_number` AND persisted
 *        `completed` / `shipped`).
 *   • "PARTIALLY SHIPPED" when the order is at `preparing` / `ready`
 *     AND has any legacy `order.shipment.tracking_number` (the PF3
 *     mirror keeps the most recent shipment on the doc). We
 *     intentionally do NOT reach into the fulfillments collection
 *     here — the derived label is a cheap cue; the authoritative
 *     partial-vs-final state lives in the PF `fulfillment_display`
 *     surfaced by the FulfillmentHistory panel below.
 *   • Never renders on pickup orders or on non-shipped statuses.
 */
function ShippedDerivedChip({ order }) {
  if (!order) return null;
  const isPickup = ((order.delivery?.code) || "").toLowerCase() === "local_pickup";
  if (isPickup) return null;
  const status = order.status;
  const persistedShip = status === "shipped";
  const persistedCompleted = status === "completed";
  const legacyTracking = !!(order.shipment && order.shipment.tracking_number);
  const isFullyShipped = persistedShip || persistedCompleted;
  const isPartially = !isFullyShipped && legacyTracking &&
    (status === "preparing" || status === "ready");
  if (!(isFullyShipped || isPartially)) return null;
  const label = isFullyShipped ? "Shipped" : "Partially Shipped";
  const testId = isFullyShipped
    ? "derived-shipped-chip"
    : "derived-partially-shipped-chip";
  const tone = isFullyShipped
    ? "bg-emerald-600 border-emerald-900 text-white"
    : "bg-blue-100 border-blue-900 text-blue-900";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold border rounded ${tone}`}
      data-testid={testId}
      title="Derived shipping status — persisted order state is unchanged."
    >
      <PackageCheck size={11} /> {label}
    </span>
  );
}

// Iter F — One-click WhatsApp for orders in `ready` status. Opens wa.me
// with a pre-filled friendly pickup message that includes the existing
// customer tracker URL — no new customer URLs created.
function WhatsAppPickupBtn({ order }) {
  const phone = String(order.customer?.phone || "").replace(/[^0-9]/g, "");
  const name = (order.customer?.name || "there").split(" ")[0];
  const isPickup = (order.delivery?.code || "") === "local_pickup";
  const trackerUrl = order.magic_token
    ? `${window.location.origin}/orders/${order.public_id}?t=${order.magic_token}`
    : `${window.location.origin}/orders/${order.public_id}`;
  const verb = isPickup ? "pick up" : "arrange shipping for";
  const message = `Hi ${name}! Your order ${order.public_id} is ready — when would you like to ${verb} it? Full details: ${trackerUrl}`;
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="btn-yellow text-sm" data-testid="btn-whatsapp-pickup">
      <MessageCircle size={14} /> WhatsApp customer
    </a>
  );
}

// Iter E — Fulfillment session panel (admin-only). Surfaces started_by /
// started_at / last_activity / duration and lets the picker resume or close
// the session manually. Auto-created by the state machine, but exposed here
// for operational visibility.
function fmtDuration(seconds) {
  if (!seconds || seconds < 1) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s || !parts.length) parts.push(`${s}s`);
  return parts.join(" ");
}

function useLiveDuration(startedAt, active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  if (!startedAt) return 0;
  return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
}

function SessionCard({ publicId, session, onChange, order }) {
  const active = session?.active;
  const history = session?.history || [];
  const liveDuration = useLiveDuration(active?.started_at, !!active);
  const [busy, setBusy] = useState(false);
  const canOpen = ["payment_verified", "preparing", "ready"].includes(order.status);

  const doResume = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${publicId}/session/resume`);
      toast.success(active ? "Session activity refreshed." : "Session opened.");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Resume failed.");
    } finally { setBusy(false); }
  };

  const doComplete = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${publicId}/session/complete`);
      toast.success("Session closed.");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Close failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className={`p-5 border-2 rounded-2xl ${active ? "border-emerald-900 bg-emerald-50" : "border-[var(--ink)] bg-white"}`} data-testid="session-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold">Fulfillment session</div>
          {active ? (
            <div className="mt-1" data-testid="session-active">
              <div className="font-display text-2xl flex items-center gap-2 text-emerald-900">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-600 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-700"></span>
                </span>
                Active
              </div>
              <div className="text-xs mt-1 text-emerald-900/80">
                Started by <b>{active.started_by}</b> · {new Date(active.started_at).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="mt-1">
              <div className="font-display text-2xl">No active session</div>
              <div className="text-xs text-[var(--smoke)] mt-1">
                {canOpen ? "Open one to start tracking picker time on this order." : "Sessions are available once payment is verified."}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {active && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900">Live duration</div>
              <div className="font-mono text-xl font-bold text-emerald-900" data-testid="session-duration">{fmtDuration(liveDuration)}</div>
            </div>
          )}
          {canOpen && (
            <div className="flex flex-col gap-2">
              <button onClick={doResume} disabled={busy} className="btn-yellow text-xs"
                      title="Refreshes your fulfillment session so the system knows you're actively working on this order."
                      data-testid="session-resume">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} {active ? "Keep session active" : "Open session"}
              </button>
              {active && (
                <button onClick={doComplete} disabled={busy} className="btn-ghost text-xs" data-testid="session-complete">
                  <PauseCircle size={12} /> Close session
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {active && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          <SessionMeta label="Last activity" value={new Date(active.last_activity_at).toLocaleTimeString()} />
          <SessionMeta label="Session id" value={<span className="font-mono">{active.id.slice(0, 8)}</span>} />
          <SessionMeta label="Started at" value={new Date(active.started_at).toLocaleTimeString()} />
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs uppercase tracking-widest font-bold cursor-pointer">Past sessions ({history.length})</summary>
          <ul className="mt-2 space-y-1 text-xs" data-testid="session-history">
            {history.map((h) => (
              <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 border border-[var(--stone)] rounded px-2 py-1 bg-white">
                <span className="font-mono opacity-70">{h.id.slice(0, 8)}</span>
                <span>{new Date(h.started_at).toLocaleString()}</span>
                <span>by {h.started_by}</span>
                <span>Duration <b>{fmtDuration(h.duration_seconds)}</b></span>
                <span className={h.status === "completed" ? "text-emerald-900" : "text-red-800"}>{h.status}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function SessionMeta({ label, value }) {
  return (
    <div className="border border-emerald-900/40 rounded p-2 bg-white">
      <div className="text-[9px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Single-order workspace
// ─────────────────────────────────────────────────────────────────────
function FulfillmentWorkspace({ publicId }) {
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [showPicking, setShowPicking] = useState(false);
  const [session, setSession] = useState({ active: null, history: [] });
  // Phase 1-ops · correction mode toggled by admin on later-stage orders.
  const [correctMode, setCorrectMode] = useState(false);
  // Phase 3A.1 — Site settings expose pickup_location_* used by the AddressBlock.
  const [settings, setSettings] = useState({});

  // Undo history — client-side ring buffer of last N destructive edits.
  const [undoStack, setUndoStack] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [r, ev, sess, st] = await Promise.all([
        api.get(`/admin/orders/${publicId}`),
        api.get(`/admin/orders/${publicId}/events`, { params: { limit: 20 } }),
        api.get(`/admin/orders/${publicId}/session`),
        api.get(`/public/settings`).catch(() => ({ data: {} })),
      ]);
      setOrder(r.data);
      setEvents(ev.data.events || []);
      setSession(sess.data);
      setSettings(st.data || {});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Order load failed.");
      nav("/admin?tab=fulfillment");
    } finally { setLoading(false); }
  }, [publicId, nav]);

  useEffect(() => { reload(); }, [reload]);

  const pushUndo = useCallback((entry) => {
    setUndoStack((s) => [entry, ...s].slice(0, 10));
    toast("Change applied.", {
      description: entry.label,
      duration: 6000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            await entry.revert();
            toast.success("Undone.");
            reload();
          } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Undo failed.");
          }
        },
      },
    });
  }, [reload]);

  if (loading || !order) {
    return (
      <div className="py-20 text-center" data-testid="workspace-loading">
        <Loader2 className="animate-spin mx-auto" size={28} /> Loading order…
      </div>
    );
  }

  // Operational Order Boundary — non-operational records (imported
  // spreadsheet / Shopify / eBay / historical / quick_sale / manual_admin
  // bookkeeping) render as a read-only audit view. Fulfillment controls
  // are hidden entirely because these rows are not part of the
  // CollectorUp.com operational fulfillment queue.
  if (order.is_operational === false) {
    return (
      <div className="space-y-6" data-testid="fulfillment-workspace-readonly">
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 md:p-6 shadow-[5px_5px_0_var(--panini-yellow)]">
          <button onClick={() => nav("/admin?tab=fulfillment")} className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid="back-to-queue">
            <ArrowLeft size={12} /> Back to queue
          </button>
          <div className="mt-2 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">
                  <span className="font-mono" data-testid="workspace-order-id">ORDER #{order.public_id}</span>
                </h2>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 border-2 border-[var(--ink)] bg-yellow-100" data-testid="non-operational-badge">
                  Non-Operational · Read-Only
                </span>
              </div>
              <div className="mt-2 text-sm opacity-80">
                Source: <b>{order.source || "Unknown"}</b>
                {order.external_source ? <> · External: <b>{order.external_source}</b></> : null}
                {order.checkout_type ? <> · Checkout: <b>{order.checkout_type}</b></> : null}
                {order.is_historical_sale ? <> · <b>Historical Entry</b></> : null}
              </div>
            </div>
          </div>
        </div>
        <div className="border-2 border-yellow-500 rounded-2xl bg-yellow-50 p-4 text-sm" data-testid="non-operational-banner">
          <div className="font-bold mb-1">This record is not part of the operational fulfillment queue.</div>
          <div className="opacity-80">
            {order.non_operational_reason || "Imported or bookkeeping sale — kept for audit and historical reporting only."}
            {" "}Fulfillment actions (picking, pickup, shipping, availability email, delivery review) are hidden here by design.
          </div>
        </div>
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5">
          <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Financial snapshot</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="opacity-60">Status</div><div className="font-mono">{order.status || "—"}</div></div>
            <div><div className="opacity-60">Total</div><div className="font-mono">${((order.pricing_snapshot?.totals?.total) ?? 0).toFixed ? order.pricing_snapshot.totals.total.toFixed(2) : (order.pricing_snapshot?.totals?.total ?? "—")}</div></div>
            <div><div className="opacity-60">Submitted</div><div className="font-mono">{order.submitted_at || "—"}</div></div>
            <div><div className="opacity-60">Entered</div><div className="font-mono">{order.created_at || "—"}</div></div>
          </div>
          <div className="mt-4 text-xs opacity-70">
            Customer: <b>{order.customer?.name || "—"}</b>
            {order.customer?.email ? <> · {order.customer.email}</> : null}
          </div>
        </div>
      </div>
    );
  }

  const canEdit = ["payment_verified", "preparing", "ready"].includes(order.status);
  // Phase 1-ops · Also allow explicit corrections at ready / shipped /
  // completed via the /fulfillment/correct endpoint.
  const canCorrect = ["ready", "shipped", "completed"].includes(order.status);
  const summary = order.fulfillment_summary || {};
  const requestedByCode = Object.fromEntries((order.requested_items || []).map((i) => [i.code, i.qty]));

  return (
    <div className="space-y-6" data-testid="fulfillment-workspace">
      {/* Phase 1d — Redesigned header: order number + customer identity
          at equal visual weight so admins can identify the order and the
          customer at a glance without hunting. */}
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 md:p-6 shadow-[5px_5px_0_var(--panini-yellow)]">
        <button onClick={() => nav("/admin?tab=fulfillment")} className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid="back-to-queue">
          <ArrowLeft size={12} /> Back to queue
        </button>
        <div className="mt-2 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">
                <span className="font-mono" data-testid="workspace-order-id">ORDER #{order.public_id}</span>
              </h2>
              <StatusChip status={order.status} />
              <ShippedDerivedChip order={order} />
              <PriorityBadge value={order.priority || "normal"} />
              {(order.variant || "white") !== "white" && (
                <OrderVariantBadge variant={order.variant || "white"} />
              )}
              {/* Feb-2026 · Missing-Sticker variant integrity. Uses the
                  backend `variant_resolution` payload (single source of
                  truth). Renders WHITE at same visual weight as BLUE/GOLD,
                  and surfaces UNKNOWN / CONFLICT warnings when the
                  resolver could not agree. Never inferred from sticker
                  code, price, or Master Sticker DB. */}
              {order.variant_resolution && (
                <VariantBadge
                  variant={order.variant_resolution.effective_variant}
                  conflict={order.variant_resolution.variant_conflict}
                  size="lg"
                  testid="order-header-variant"
                />
              )}
              {/* Phase 3A.1 — Pickup/Shipping badge, prominent next to the order # */}
              <FulfillmentMethodBadge group={order.fulfillment_group} method={order.fulfillment_method} />
              {/* Phase 1 · Manual "Paid via" badge — reuses payment.method
                  to show the exact payment channel (Cash / Card / Zelle /
                  Cash App / Pay In Store) next to the fulfillment badges.
                  Also renders for the queue lookup path (fallback: legacy
                  "manual" method label). Hidden when no payment has been
                  recorded yet. */}
              <PaidViaBadge order={order} />
              {order.creation_method === "manual" && (
                <ManualSourceBadge source={order.source} size="md" testid="workspace-manual-badge" />
              )}
            </div>
            {/* Customer identity — nearly the same weight as the order number */}
            <div className="mt-3 space-y-1">
              <div className="font-display text-2xl md:text-3xl font-bold" data-testid="workspace-customer-name">
                {order.customer?.name || "(no name)"}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-base md:text-lg font-mono break-all" data-testid="workspace-customer-email">
                  {order.customer?.email || "—"}
                </div>
                {order.customer?.email && (
                  <CopyEmailButton email={order.customer.email} />
                )}
              </div>
              <div className="flex items-center gap-2" data-testid="workspace-customer-phone-row">
                <div className="text-base md:text-lg font-mono" data-testid="workspace-customer-phone">
                  {order.customer?.phone || "—"}
                </div>
                <WhatsAppButton order={order} />
              </div>
              <CustomerSocialsRow order={order} />
            </div>
            {/* Phase 3A.1 — Pickup location or Ship-to block */}
            <AddressBlock order={order} pickupSettings={settings} />
            {/* Iter S · Stage 1 — Order Information (placed / updated) */}
            <OrderInformationSection order={order} />
          </div>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            {["payment_verified", "preparing", "ready", "completed"].includes(order.status) && (
              <button onClick={() => setShowPicking(true)} className="btn-yellow text-sm" data-testid="btn-picking-list">
                <Printer size={14} /> Picking list
              </button>
            )}
            <StatusActions order={order} onDone={reload} pushUndo={pushUndo} />
          </div>
        </div>
      </div>

      {showPicking && <PickingListModal publicId={publicId} onClose={() => setShowPicking(false)} />}

      {/* Iter E — Fulfillment session panel (admin only) */}
      <SessionCard publicId={publicId} session={session} onChange={reload} order={order} />

      {/* Section 1 — Customer Instructions */}
      <PinnedInstructionsCard order={order} onDone={reload} />

      {/* Section 2 — Fulfillment Summary */}
      <FulfillmentSummaryCard summary={summary} snap={order.pricing_snapshot} />

      {/* Section 2b — Customer Invoice (frozen at order placement) */}
      <CustomerInvoiceCard order={order} snap={order.pricing_snapshot} onSaved={reload} />

      {(() => {
        const pay = order?.payment || {};
        const pr  = order?.payment_reconciliation || {};
        const effectiveMethod = getEffectivePaymentMethod(pay);
        const verified = isPaymentVerified(pay);
        const showZelle = shouldShowZelleReconciliation(pay);
        return (
          <>
            {showZelle && (
              <div className="mt-3 space-y-2 text-xs" data-testid="payment-identities">
                <div>
                  <div className="font-bold uppercase tracking-widest text-[var(--smoke)]">Order customer</div>
                  <div data-testid="pi-order-customer">{order?.customer?.name || "—"}</div>
                </div>
                <div>
                  <div className="font-bold uppercase tracking-widest text-[var(--smoke)]">Zelle payment name entered by customer</div>
                  <div data-testid="pi-customer-zelle-name">{pr.customer_entered_zelle_name || "Not provided"}</div>
                </div>
                <div>
                  <div className="font-bold uppercase tracking-widest text-[var(--smoke)]">Matched Zelle transaction sender</div>
                  <div data-testid="pi-matched-sender">{pr.zelle_sender_name || "Not matched"}</div>
                </div>
              </div>
            )}
            {showZelle
              ? <ZellePaymentVerificationCard order={order} onSaved={reload} />
              : null}
            {verified && (
              <div className="mt-3 rounded-2xl border-4 border-[var(--ink)] bg-emerald-50 p-4"
                    data-testid="payment-verified-summary">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Payment verified</div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="text-[var(--smoke)]">Actual method</div>
                  <div data-testid="pv-method">{METHOD_LABELS[effectiveMethod] || effectiveMethod || "—"}</div>
                  <div className="text-[var(--smoke)]">Amount received</div>
                  <div data-testid="pv-amount">${Number(pay.amount_received ?? 0).toFixed(2)}</div>
                  <div className="text-[var(--smoke)]">Received</div>
                  <div data-testid="pv-received-at">{pay.received_at || "—"}</div>
                  <div className="text-[var(--smoke)]">Verified</div>
                  <div data-testid="pv-verified-at">{pay.verified_at || "—"}</div>
                  <div className="text-[var(--smoke)]">Verified by</div>
                  <div data-testid="pv-verified-by">{pay.verified_by || "—"}</div>
                  {pay.reference_id && (<><div className="text-[var(--smoke)]">Reference</div><div data-testid="pv-reference">{pay.reference_id}</div></>)}
                  <div className="text-[var(--smoke)]">Balance</div>
                  <div data-testid="pv-balance">${Number(pay.balance_due ?? 0).toFixed(2)}</div>
                  {pay.overpayment_amount > 0 && (<><div className="text-[var(--smoke)]">Overpayment</div><div data-testid="pv-overpayment">${Number(pay.overpayment_amount).toFixed(2)}</div></>)}
                  {pay.payment_method_original && pay.payment_method_original !== effectiveMethod && (
                    <><div className="text-[var(--smoke)]">Originally selected</div><div data-testid="pv-original">{METHOD_LABELS[pay.payment_method_original] || pay.payment_method_original}</div></>
                  )}
                  {effectiveMethod === "zelle" && (<>
                    <div className="text-[var(--smoke)]">Zelle payer (customer)</div>
                    <div data-testid="pv-zelle-customer">{pr.customer_entered_zelle_name || "Not provided"}</div>
                    <div className="text-[var(--smoke)]">Zelle sender (matched)</div>
                    <div data-testid="pv-zelle-sender">{pr.zelle_sender_name || "Not matched"}</div>
                    {pr.zelle_reference && (<><div className="text-[var(--smoke)]">Zelle memo / ref</div><div data-testid="pv-zelle-ref">{pr.zelle_reference}</div></>)}
                  </>)}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Section 3a — PHASE 1B PICKING CHECKLIST (primary workflow) */}
      <PickingChecklistCard
        order={order}
        onSaved={reload}
        canEdit={canEdit || correctMode}
        correctMode={correctMode}
        onExitCorrect={() => setCorrectMode(false)}
      />
      {canCorrect && !correctMode && (
        <div className="rounded-2xl border-2 border-amber-700 bg-amber-50 p-4 flex items-start gap-3" data-testid="correct-picking-panel">
          <ClipboardCheck className="shrink-0 text-amber-900 mt-0.5" size={18} />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-bold text-amber-900">Need to correct this checklist?</div>
            <div className="text-amber-900/80">
              The order is at <span className="font-mono">{order.status}</span>. Reopen the checklist to fix a missed item without changing the order status.
              Every correction is recorded in the timeline.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(
                `Reopen the picking checklist for order ${order.public_id}? ` +
                `The order will stay at "${order.status}" — status will not change ` +
                `automatically. A correction event will be recorded.`
              )) setCorrectMode(true);
            }}
            className="btn-yellow text-xs shrink-0"
            data-testid="btn-correct-picking"
          >
            <RotateCcw size={12} /> Correct picking
          </button>
        </div>
      )}

      {/* Section 3b — WAITING LIST (advanced view — kept for parity with prior UX) */}
      <details className="border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="advanced-lists">
        <summary className="cursor-pointer px-5 py-3 font-display text-lg">Advanced list editors (waiting / removed)</summary>
        <div className="p-5 space-y-6 border-t-2 border-[var(--ink)]">
          <ListEditorCard
            title="Waiting List"
            subtitle="Legacy exception-based editor. Prefer the checklist above."
            list="pending"
            items={order.pending_items || []}
            requestedByCode={requestedByCode}
            otherItems={order.removed_items || []}
            canEdit={canEdit}
            publicId={publicId}
            onSaved={reload}
            pushUndo={pushUndo}
            testid="waiting-list"
          />
          <ListEditorCard
            title="Removed List"
            subtitle="Legacy exception-based editor."
            list="removed"
            items={order.removed_items || []}
            requestedByCode={requestedByCode}
            otherItems={order.pending_items || []}
            canEdit={canEdit}
            publicId={publicId}
            onSaved={reload}
            pushUndo={pushUndo}
            testid="removed-list"
          />
        </div>
      </details>

      {/* Section 5 — REQUESTED REFERENCE (read-only) */}
      <RequestedReferenceCard order={order} />

      {/* Section 5b — Notification History (Iter P) */}
      <NotificationHistoryCard order={order} onRefresh={reload} />

      {/* PF2 — Fulfillment History (partial pickup / partial shipment timeline) */}
      <FulfillmentHistoryContainer order={order} onChanged={reload} />

      {/* P0 · Refund History — refreshes on `reload` which every refund
          modal calls via its `onSuccess`. Read-only card. */}
      <RefundHistoryCard order={order} refreshKey={events?.length || 0} />

      {/* Availability follow-up email — customer-facing */}
      <AvailabilityEmailCard order={order} onDone={reload} />

      {/* Section 6 — Events / undo log */}
      <EventsCard events={events} />

      {/* Section 7 — Phase 1b §14 · Archive / Delete Test Order */}
      <DangerZoneCard order={order} onDone={reload} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Status transition buttons
// ─────────────────────────────────────────────────────────────────────
function StatusActions({ order, onDone, pushUndo }) {
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showLegacyCancel, setShowLegacyCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  // P0 · Record Refund + Refund & Cancel (2026-02-16). Manual refund
  // workflow (no processor). Order.status is NEVER touched by Refund
  // Only; Refund & Cancel writes both events atomically at the UI layer.
  const [showRefund, setShowRefund] = useState(false);
  const [showRefundAndCancel, setShowRefundAndCancel] = useState(false);
  // `isPaid` = the order has moved past payment verification. The admin
  // detail API doesn't reliably include the `payment` sub-doc, so we
  // read the operational status instead — every post-verify status
  // implies a verified payment lives on the ORDER row.
  const PAID_STATUSES = new Set([
    "payment_verified", "preparing", "ready", "shipped", "completed",
    "partially_refunded", "refunded",
  ]);
  const isPaid = PAID_STATUSES.has(order?.status);
  const orderTotal = Number(
    order?.pricing_snapshot?.totals?.final_total
    ?? order?.pricing_snapshot?.totals?.total
    ?? 0
  );

  const setStatus = async (target, reason, extra = {}) => {
    setBusy(true);
    const prev = order.status;
    try {
      await api.post(`/admin/orders/${order.public_id}/status`, { target, reason, ...extra });
      toast.success(`Status → ${target.replace("_", " ")}`);
      // Only offer undo for non-destructive forward transitions (not cancelled/completed)
      if (target !== "cancelled" && target !== "completed") {
        pushUndo({
          label: `Status: ${prev} → ${target}`,
          revert: () => api.post(`/admin/orders/${order.public_id}/status`, {
            target: prev === "payment_verified" ? "preparing" : prev,
            // The API enforces legal edges — reverting a forward transition
            // is only allowed via the state machine's cancel/rewind paths.
          }).catch(() => {}),
        });
      }
      onDone();
    } catch (e) {
      // Per-Unit Picking · B.1 · Hard-block modal even when the
      // client-side check missed (e.g., a stale summary). The backend
      // returns a structured detail dict {code:"picking_incomplete",
      // requested_total, ready_total, remaining_total}.
      const _detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 409 &&
        _detail && typeof _detail === "object" &&
        _detail.code === "picking_incomplete"
      ) {
        setIncompleteBlock({
          ready: Number(_detail.ready_total || 0),
          requested: Number(_detail.requested_total || 0),
          remaining: Number(_detail.remaining_total || 0),
        });
      } else {
        toast.error(formatApiError(_detail) || "Transition failed.");
      }
    } finally { setBusy(false); }
  };

  const verifyPayment = async (extra = {}) => {
    setBusy(true);
    try {
      // Client-generated idempotency key protects against double-clicks
      // and repeated network retries at the transport layer.
      const idem = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `pv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      await api.post(`/admin/orders/${order.public_id}/verify-payment`,
                     { ...extra, idempotency_key: idem });
      toast.success("Payment recorded.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Verify failed.");
    } finally { setBusy(false); }
  };

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Phase 3A.2 — Send Payment Reminder modal (awaiting_payment only)
  const [showReminderModal, setShowReminderModal] = useState(false);
  // Shipping & Fulfillment · Phase 2 — Mark-as-Shipped modal (UPS + USPS).
  const [showShipModal, setShowShipModal] = useState(false);
  // PF2 · Partial Pickup — Record Pickup allocation panel.
  const [showRecordPickup, setShowRecordPickup] = useState(false);
  // Live per-code fulfilled totals so RecordPickupModal can compute
  // `available_now = qty_picked - already_fulfilled` accurately when a
  // second (or later) partial event is being recorded. Uses the same
  // hook the Fulfillment History panel uses (single source of truth
  // via GET /admin/orders/{public_id}/fulfillments).
  const { data: _pfData } = useFulfillments(order?.public_id);
  const fulfilledByCode = _pfData?.totals?.fulfilled_by_code
    || order?.fulfillment_totals?.fulfilled_by_code
    || {};
  // Per-Unit Picking · B.1 hard-block modal state. When set, the admin
  // clicked "Mark ready" while physical picking was incomplete. There
  // is NO "Continue anyway" affordance — the modal only offers Close.
  const [incompleteBlock, setIncompleteBlock] = useState(null);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(order.status === "awaiting_payment" || order.status === "payment_submitted") && (
        <>
          {order.status === "awaiting_payment" && (
            <>
              <button onClick={() => setShowReminderModal(true)} disabled={busy}
                       className="btn-ghost text-sm" data-testid="btn-send-payment-reminder">
                <Send size={14} /> Send payment reminder
              </button>
              {showReminderModal && (
                <PaymentReminderModal
                  publicId={order.public_id}
                  onClose={() => setShowReminderModal(false)}
                  onSent={() => onDone()}
                />
              )}
            </>
          )}
          <button onClick={() => setShowPaymentModal(true)} disabled={busy}
                   className="btn-red text-sm" data-testid="btn-record-payment">
            <PackageCheck size={14} /> Record / verify payment
          </button>
          {showPaymentModal && (
            <PaymentConfirmationModal
              order={order}
              onCancel={() => setShowPaymentModal(false)}
              onConfirm={async (payload) => {
                setShowPaymentModal(false);
                await verifyPayment(payload);
              }}
            />
          )}
        </>
      )}
      {order.status === "payment_verified" && (
        <button onClick={() => setStatus("preparing")} disabled={busy} className="btn-yellow text-sm" data-testid="btn-start-preparing">
          <PlayCircle size={14} /> Start preparing
        </button>
      )}
      {order.status === "preparing" && (() => {
        const isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
        const _sum = order.fulfillment_summary || {};
        const pct = Number(_sum.percent_complete || 0);
        const ready = Number(_sum.ready_total || 0);
        const requested = Number(_sum.requested_total || 0);
        // Owner P0 · Shipping-workflow repair (scenario 3).
        // `remaining_to_fulfill_total` is the number of stickers that
        // have been PICKED but not yet shipped/handed-over. That's the
        // exact qty a partial shipment can carry today — never `ready
        // - requested` (which stays 195 even after Shipment #1 of 195
        // has been dispatched). When positive AND less than the full
        // requested set, we expose a direct partial-shipment CTA so
        // the owner never has to force the whole order through READY
        // just to move the ready items. The PF endpoint has never
        // gated on `status`, so this is a pure UI unlock. Pickup
        // orders keep the legacy single-CTA flow because pickup
        // handoffs typically happen once the order is fully picked.
        const fulfilled = Number(_sum.fulfilled_total || 0);
        const remainingToFulfill = Number(
          _sum.remaining_to_fulfill_total ?? Math.max(0, ready - fulfilled)
        );
        const priorShipments = Number(_sum.fulfillments_count || 0) > 0;
        const canPartialShip = !isPickup
          && remainingToFulfill > 0
          && remainingToFulfill < requested;
        // Partial-Pickup UX Unlock (2026-02-16) — mirror the partial-ship
        // CTA so a LOCAL PICKUP order in `preparing` can record a real
        // pickup event for the N picked-but-unfulfilled stickers without
        // being forced through the whole-order 100% B.1 gate. The PF
        // pickup endpoint is already unauthenticated of order.status and
        // enforces the "batch ≤ picked − already_fulfilled" invariant in
        // `assert_allocations_within_picked`.
        const canPartialPickup = isPickup
          && remainingToFulfill > 0
          && remainingToFulfill < requested;
        const partialLabel = priorShipments || fulfilled > 0
          ? `Ship Remaining Items (${remainingToFulfill})`
          : `Ship Ready Items (${remainingToFulfill})`;
        const partialPickupLabel = priorShipments || fulfilled > 0
          ? `Record Remaining Pickup (${remainingToFulfill})`
          : `Record Partial Pickup — ${remainingToFulfill} Ready`;
        const partialTestId = priorShipments || fulfilled > 0
          ? "btn-ship-remaining-items"
          : "btn-create-partial-shipment";
        const partialPickupTestId = priorShipments || fulfilled > 0
          ? "btn-record-remaining-pickup"
          : "btn-record-partial-pickup";
        return (
          <>
            {canPartialShip && (
              <button
                onClick={() => setShowRecordPickup(true)}
                disabled={busy}
                className="btn-red text-sm"
                data-testid={partialTestId}
                title={
                  priorShipments || fulfilled > 0
                    ? `${remainingToFulfill} picked stickers are ready to ship; ${requested - fulfilled - remainingToFulfill} still pending pick.`
                    : `Ship the ${remainingToFulfill} picked stickers now; the remaining ${requested - remainingToFulfill} stay open.`
                }
              >
                <PackageCheck size={14} /> {partialLabel}
              </button>
            )}
            {canPartialPickup && (
              <button
                onClick={() => setShowRecordPickup(true)}
                disabled={busy}
                className="btn-red text-sm"
                data-testid={partialPickupTestId}
                title={
                  priorShipments || fulfilled > 0
                    ? `${remainingToFulfill} picked stickers are ready for pickup; ${requested - fulfilled - remainingToFulfill} still pending pick.`
                    : `Hand off the ${remainingToFulfill} picked stickers now; the remaining ${requested - remainingToFulfill} stay open.`
                }
              >
                <PackageCheck size={14} /> {partialPickupLabel}
              </button>
            )}
            <button
              onClick={() => {
                // Per-Unit Picking · Hard Completion Gate (B.1) —
                // NO "Continue anyway" path. If any physical unit is
                // unpicked, refuse the transition entirely and surface
                // a hard-block modal. Backend enforces the same rule
                // (409 picking_incomplete); the modal here just avoids
                // an unnecessary round-trip.
                if (requested <= 0 || ready < requested) {
                  setIncompleteBlock({
                    ready, requested,
                    remaining: Math.max(0, requested - ready),
                  });
                  return;
                }
                setStatus("ready");
              }}
              disabled={busy}
              className={(canPartialShip || canPartialPickup) ? "btn-ghost text-sm" : "btn-yellow text-sm"}
              data-testid="btn-mark-ready"
            >
              <CheckCircle2 size={14} /> {isPickup ? "Mark ready for pickup" : "Mark ready to ship"}
            </button>
          </>
        );
      })()}
      {order.status === "ready" && (() => {
        // Phase 1b — delivery-specific completion. Pickup uses a dedicated
        // endpoint; ship uses the new Phase 2 shipment modal that captures
        // carrier + tracking and previews the customer email before sending.
        // PF2 · Pickup now opens the Record Pickup allocation panel so
        // partial handoffs are captured. The legacy `Mark as picked up`
        // one-shot endpoint stays intact server-side for compatibility.
        const isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
        if (isPickup) {
          return (
            <button
              onClick={() => setShowRecordPickup(true)}
              disabled={busy}
              className="btn-red text-sm"
              data-testid="btn-record-pickup"
            >
              <PackageCheck size={14} /> Record Pickup
            </button>
          );
        }
        return (
          <button
            onClick={() => setShowRecordPickup(true)}
            disabled={busy}
            className="btn-red text-sm"
            data-testid="btn-create-shipment"
          >
            <PackageCheck size={14} /> Create Shipment
          </button>
        );
      })()}
      {/* Owner P0 · Shipping-workflow repair (scenario 2).
          When a shipping order has been fully fulfilled — either the
          persisted state moved to `shipped` (legacy path) or the PF
          endpoint promoted it to `completed` — the owner must always
          have a prominent "Shipment details" control. Previously the
          button only rendered on `status === "shipped"`, so any order
          that terminally completed via a PF `final_shipment` looked
          like a plain "Completed" order with no visible tracking. */}
      {(() => {
        const isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
        if (isPickup) return null;
        const persistedShip = order.status === "shipped";
        const legacyShip = !!(order.shipment && order.shipment.tracking_number);
        const completedShip = order.status === "completed";
        // Show whenever the order is a shipping order AND there is
        // ANY shipment evidence (legacy tracking, `shipped` state, or
        // a `completed` shipping order which by definition has ≥1 PF
        // shipment event).
        if (!(persistedShip || legacyShip || completedShip)) return null;
        const openShipmentDetails = () => {
          // Legacy `shipped` path — surface the historic ShipmentModal
          // (with email preview + resend) so admins on old orders keep
          // the workflow they already know. Everything else opens the
          // PF `FulfillmentHistory` panel where each shipment event
          // already renders carrier + tracking + link + timestamps +
          // quantities + partial/final status.
          if (persistedShip) {
            setShowShipModal(true);
            return;
          }
          const target = document.querySelector(
            '[data-testid="fulfillment-history-card"]'
          );
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            target.setAttribute("data-highlight", "1");
            window.setTimeout(() => {
              try { target.removeAttribute("data-highlight"); } catch { /* ignore */ }
            }, 1600);
          }
        };
        return (
          <button
            onClick={openShipmentDetails}
            disabled={busy}
            className="btn-ghost text-sm"
            data-testid="btn-view-shipment"
          >
            <PackageCheck size={14} /> Shipment details
          </button>
        );
      })()}
      {["preparing", "ready"].includes(order.status) && (order.customer?.phone || "").trim() && (
        <WhatsAppMsgBtn order={order} kind={order.status === "ready" ? "ready" : "ready"} />
      )}
      {/* Unpaid-abandoned Cancel — restricted to states where payment has NOT
          been verified. Sends unpaid_abandoned=true so the backend can enforce
          the same restriction defense-in-depth (see admin ops audit 2026-02-16). */}
      {["awaiting_payment", "payment_submitted"].includes(order.status) && (
        <button onClick={() => setShowCancel(true)} className="btn-ghost text-sm text-red-800" data-testid="btn-cancel-order">
          <Ban size={14} /> Cancel order
        </button>
      )}

      {showCancel && (
        <CancelOrderModal
          order={order}
          busy={busy}
          onClose={() => { setShowCancel(false); setCancelReason(""); }}
          onConfirm={async (reasonText) => {
            await setStatus("cancelled", reasonText, { unpaid_abandoned: true });
            setShowCancel(false); setCancelReason("");
          }}
        />
      )}

      {/* Pre-existing paid-state cancel — untouched behaviour (free-text reason,
          no unpaid_abandoned flag). The dedicated paid-cancel / refund
          workflow is a separate future task. */}
      {["payment_verified", "preparing", "ready"].includes(order.status) && (
        <button onClick={() => setShowLegacyCancel(true)} className="btn-ghost text-sm text-red-800" data-testid="btn-cancel">
          <Ban size={14} /> Cancel
        </button>
      )}

      {/* P0 · Record Refund — manual bookkeeping only (2026-02-16).
          Visible whenever the order has a verified payment (any post-
          verification status). Does NOT alter order.status. */}
      {isPaid && (
        <button onClick={() => setShowRefund(true)} className="btn-ghost text-sm text-red-800" data-testid="btn-record-refund">
          <DollarSign size={14} /> Record Refund
        </button>
      )}

      {/* P0 · Refund & Cancel — composite. Runs full-remaining refund
          then cancel; on cancel-failure surfaces a Retry Cancel path
          that reuses the same refund idempotency key. */}
      {isPaid && ["payment_verified", "preparing", "ready"].includes(order.status) && (
        <button onClick={() => setShowRefundAndCancel(true)} className="btn-ghost text-sm text-red-800" data-testid="btn-refund-and-cancel">
          <PackageX size={14} /> Refund &amp; Cancel
        </button>
      )}

      {showRefund && (
        <RefundModal
          order={order}
          onClose={() => setShowRefund(false)}
          onSuccess={() => { setShowRefund(false); onDone(); }}
        />
      )}
      {showRefundAndCancel && (
        <RefundAndCancelModal
          order={order}
          onClose={() => setShowRefundAndCancel(false)}
          onSuccess={() => { setShowRefundAndCancel(false); onDone(); }}
        />
      )}

      {showLegacyCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="cancel-modal">
          <div className="w-full max-w-md bg-white border-2 border-[var(--ink)] rounded-2xl p-6 shadow-[8px_8px_0_var(--ink)]">
            <h3 className="font-display text-xl">Cancel order {order.public_id}?</h3>
            {isPaid ? (
              <div className="mt-3 text-sm border border-yellow-500 bg-yellow-50 text-yellow-900 rounded-md p-3"
                   data-testid="cancel-paid-warning">
                This order has recorded payment of <b>${orderTotal.toFixed(2)}</b>.
                Cancelling will NOT record or issue a refund. Use <b>Refund &amp; Cancel</b> if the customer is also being refunded.
              </div>
            ) : (
              <p className="text-sm text-[var(--smoke)] mt-2">This is destructive. Enter a short reason (≥ 4 characters).</p>
            )}
            <textarea
              className="w-full mt-3 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason (≥ 4 characters) — e.g. Customer stopped responding."
              data-testid="cancel-reason-input"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button className="btn-ghost text-sm" onClick={() => setShowLegacyCancel(false)} data-testid="cancel-modal-close">Nevermind</button>
              <button
                className="btn-red text-sm"
                disabled={busy || cancelReason.trim().length < 4}
                data-testid="cancel-confirm"
                onClick={async () => { await setStatus("cancelled", cancelReason.trim()); setShowLegacyCancel(false); }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Confirm cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showShipModal && (
        <ShipmentModal
          order={order}
          onClose={() => setShowShipModal(false)}
          onDone={() => { setShowShipModal(false); onDone(); }}
        />
      )}

      {showRecordPickup && (
        <RecordPickupModal
          order={order}
          mode={(order?.delivery?.code || "").toLowerCase() === "local_pickup" ? "pickup" : "shipping"}
          fulfilledByCode={fulfilledByCode}
          onClose={() => setShowRecordPickup(false)}
          onSuccess={() => { setShowRecordPickup(false); onDone(); }}
        />
      )}

      {incompleteBlock && (() => {
        // Partial-Pickup UX Unlock (2026-02-16) — when a LOCAL PICKUP
        // order is 100%-gated but has picked-but-unfulfilled stickers,
        // offer a secondary "Record Partial Pickup" affordance so the
        // owner isn't stuck at a dead-end modal. Shipping orders keep
        // the whole-order gate as-is because the preparing view already
        // exposes "Ship Ready Items".
        const _sum = order.fulfillment_summary || {};
        const _fulfilled = Number(_sum.fulfilled_total || 0);
        const _remaining = Number(
          _sum.remaining_to_fulfill_total ?? Math.max(0, incompleteBlock.ready - _fulfilled),
        );
        const _isPickup = (order.delivery?.code || "").toLowerCase() === "local_pickup";
        const showPartialAction = _isPickup && _remaining > 0;
        return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          data-testid="picking-incomplete-block"
          role="dialog"
          aria-modal="true"
          aria-labelledby="picking-incomplete-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3
              id="picking-incomplete-title"
              className="text-lg font-semibold text-red-700"
              data-testid="picking-incomplete-title"
            >
              Picking incomplete
            </h3>
            <p className="mt-3 text-sm text-slate-700" data-testid="picking-incomplete-summary">
              <span className="font-mono font-semibold">
                {incompleteBlock.ready} of {incompleteBlock.requested}
              </span>{" "}
              stickers picked
            </p>
            <p className="mt-1 text-sm text-slate-700" data-testid="picking-incomplete-remaining">
              <span className="font-mono font-semibold">{incompleteBlock.remaining}</span>{" "}
              sticker{incompleteBlock.remaining === 1 ? "" : "s"} remaining
            </p>
            <p className="mt-4 text-xs text-slate-500">
              The order cannot be marked ready until every physical unit is checked.
              Finish picking the remaining stickers below and try again.
            </p>
            {showPartialAction && (
              <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-600" data-testid="picking-incomplete-partial-hint">
                  You can hand off the picked stickers now and keep the rest open on the same order.
                </p>
                <button
                  type="button"
                  className="btn-red text-sm mt-3"
                  data-testid="picking-incomplete-partial-pickup"
                  onClick={() => { setIncompleteBlock(null); setShowRecordPickup(true); }}
                >
                  <PackageCheck size={14} /> Record Partial Pickup of {_remaining} Ready Sticker{_remaining === 1 ? "" : "s"}
                </button>
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="btn-ghost text-sm"
                data-testid="picking-incomplete-close"
                onClick={() => setIncompleteBlock(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// PF2 · Fulfillment History container — wraps the history strip with
// a lightweight fetch so the parent workspace doesn't need to know
// about the endpoint. Reload propagates back to the parent through
// `onChanged` so the summary card and status badges refresh together.
function FulfillmentHistoryContainer({ order, onChanged }) {
  const { data, refresh } = useFulfillments(order?.public_id);
  const handleChanged = React.useCallback(() => {
    refresh();
    onChanged?.();
  }, [refresh, onChanged]);
  return (
    <FulfillmentHistory
      order={order}
      data={data}
      onChanged={handleChanged}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Pinned Instructions (Customer note editable by admin)
// ─────────────────────────────────────────────────────────────────────
function PinnedInstructionsCard({ order, onDone }) {
  const [value, setValue] = useState(order.customer?.pinned_instructions || "");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setValue(order.customer?.pinned_instructions || ""); setDirty(false); }, [order]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.public_id}/pinned-instructions`, { pinned_instructions: value });
      toast.success("Pinned instructions saved.");
      setDirty(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)]" data-testid="pinned-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold">Customer instructions (pinned)</div>
        {dirty && (
          <button onClick={save} disabled={busy} className="btn-yellow text-xs" data-testid="pinned-save">
            {busy ? <Loader2 size={12} className="animate-spin" /> : null} Save
          </button>
        )}
      </div>
      <textarea
        className="w-full mt-2 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm bg-white"
        rows={2}
        value={value}
        placeholder="Add a note visible on the customer's payment page…"
        onChange={(e) => { setValue(e.target.value); setDirty(true); }}
        maxLength={500}
        data-testid="pinned-input"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Shipment Modal (Phase 2)
//
//  Two-step: (1) enter tracking + save shipment, (2) preview email +
//  explicit confirm. Local Pickup orders never reach this modal because
//  the ready-state button branch renders "Mark as picked up" instead.
// ─────────────────────────────────────────────────────────────────────
function ShipmentModal({ order, onClose, onDone }) {
  const deliveryCode = ((order.delivery?.code) || "").toLowerCase();
  const isUsps = deliveryCode === "usps_stamp";
  const isPickup = deliveryCode === "local_pickup";
  const alreadyShipped = order.status === "shipped";
  const existingShip = order.shipment || {};
  const [step, setStep] = React.useState(alreadyShipped ? "preview" : "capture");
  const [tracking, setTracking] = React.useState(existingShip.tracking_number || "");
  const [note, setNote] = React.useState(existingShip.shipment_note || "");
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [sendState, setSendState] = React.useState(null); // { sent, duplicate, sent_at }

  const carrier = isUsps ? "USPS Stamp" : "UPS Ground";
  const totals = order?.pricing_snapshot?.totals || {};
  const addr = order?.customer?.shipping_address || null;
  const custName = [order?.customer?.first_name, order?.customer?.last_name].filter(Boolean).join(" ") || order?.customer?.name || "—";
  const custEmail = order?.customer?.email || "—";
  const ack = order?.usps_acknowledgment || null;

  const doPreview = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/orders/${order.public_id}/shipment-email/preview`);
      setPreview(r.data);
      setStep("preview");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Preview failed.");
    } finally { setBusy(false); }
  }, [order.public_id]);

  React.useEffect(() => {
    if (step === "preview" && alreadyShipped && !preview) doPreview();
  }, [step, alreadyShipped, preview, doPreview]);

  if (isPickup) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="shipment-modal-pickup-blocked">
        <div className="w-full max-w-md bg-white border-2 border-[var(--ink)] rounded-2xl p-6">
          <h3 className="font-display text-xl">This order is Local Pickup.</h3>
          <p className="text-sm text-[var(--smoke)] mt-2">
            Local Pickup orders use the pickup workflow — they should not be marked as shipped.
          </p>
          <div className="mt-4 flex justify-end">
            <button className="btn-red text-sm" onClick={onClose} data-testid="shipment-modal-close">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    if (!isUsps) {
      const trimmed = tracking.trim();
      if (!trimmed) {
        toast.error("Tracking number is required for UPS Ground shipments.");
        return;
      }
      if (trimmed.length > 100) {
        toast.error("Tracking number is too long.");
        return;
      }
      if (/[<>]/.test(trimmed)) {
        toast.error("Tracking number contains unsafe characters.");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.public_id}/status`, {
        target: "shipped",
        carrier,
        tracking_number: isUsps ? "" : tracking.trim(),
        shipment_note: note.trim(),
        suppress_email: true,
      });
      toast.success("Shipment saved.");
      await doPreview();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save shipment failed.");
    } finally { setBusy(false); }
  };

  const handleSend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/orders/${order.public_id}/shipment-email/send`);
      setSendState(r.data);
      if (r.data?.duplicate) toast.info("Shipment email already sent — not resending.");
      else toast.success("Shipment email sent.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Send failed — shipment details are still saved.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" role="dialog" data-testid="shipment-modal">
      <div className="w-full max-w-2xl bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--ink)] my-8">
        <header className="p-5 border-b-2 border-[var(--ink)]">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">Mark as Shipped</div>
              <h3 className="font-display text-2xl">{carrier}</h3>
            </div>
            <span className="text-xs font-mono text-[var(--smoke)]" data-testid="shipment-modal-order-id">
              Order {order.public_id}
            </span>
          </div>
        </header>

        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-testid="shipment-modal-customer">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Customer</div>
              <div className="mt-1 font-bold">{custName}</div>
              <div>{custEmail}</div>
              <div>{order?.customer?.phone || "—"}</div>
            </div>
            <div data-testid="shipment-modal-order-meta">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Order</div>
              <div className="mt-1"><span className="font-bold">Delivery:</span> {order?.delivery?.label || deliveryCode}</div>
              <div><span className="font-bold">Fulfillment:</span> {order.status}</div>
              <div><span className="font-bold">Paid total:</span> ${Number(totals.total || 0).toFixed(2)}</div>
            </div>
          </div>

          {addr && (
            <div data-testid="shipment-modal-address">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Ship to</div>
              <div className="mt-1">
                {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}, {addr.city}, {addr.state} {addr.postal_code}, {addr.country}
              </div>
            </div>
          )}

          {isUsps && ack && (
            <div className="rounded-xl border-2 border-[var(--panini-red)] bg-red-50 p-3 text-xs" data-testid="shipment-modal-ack">
              <div className="font-bold text-[var(--panini-red)]">USPS Stamp risk acknowledged by customer</div>
              <div className="mt-1">
                {ack.acknowledged_at ? `Acknowledged at ${new Date(ack.acknowledged_at).toLocaleString()}` : "Acknowledged."}
              </div>
              {ack.text && <div className="mt-1 italic">&ldquo;{ack.text}&rdquo;</div>}
            </div>
          )}

          {step === "capture" && (
            <>
              {isUsps ? (
                <div className="rounded-xl border-2 border-[var(--ink)] bg-neutral-50 p-3" data-testid="shipment-modal-usps-notice">
                  <div className="font-bold">Shipping type: Stamp / Untracked</div>
                  <p className="text-xs mt-1">This shipment does not include tracking.</p>
                </div>
              ) : (
                <label className="block" data-testid="shipment-modal-tracking-field">
                  <span className="text-[10px] uppercase tracking-widest font-bold">Tracking number *</span>
                  <input
                    type="text"
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="1Z 999 999 99 9999 999 9"
                    maxLength={100}
                    className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm font-mono"
                    data-testid="shipment-modal-tracking-input"
                  />
                </label>
              )}
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold">Shipment note (optional)</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
                  placeholder="Anything the packer wants to record."
                  data-testid="shipment-modal-note-input"
                />
              </label>
            </>
          )}

          {step === "preview" && (
            <div data-testid="shipment-modal-preview">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Email preview</div>
              {!preview ? (
                <div className="mt-2 text-xs italic text-[var(--smoke)]">Loading preview…</div>
              ) : (
                <div className="mt-2 space-y-2">
                  <div><span className="font-bold text-xs">To:</span> <span data-testid="shipment-modal-preview-recipient">{preview.recipient}</span></div>
                  <div><span className="font-bold text-xs">Subject:</span> <span data-testid="shipment-modal-preview-subject">{preview.subject}</span></div>
                  <div><span className="font-bold text-xs">Carrier:</span> <span data-testid="shipment-modal-preview-carrier">{preview.carrier || carrier}</span></div>
                  {preview.tracking_number && (
                    <div><span className="font-bold text-xs">Tracking:</span> <span className="font-mono" data-testid="shipment-modal-preview-tracking">{preview.tracking_number}</span></div>
                  )}
                  <div
                    className="mt-2 border-2 border-[var(--stone)] rounded-lg p-3 max-h-64 overflow-y-auto bg-white"
                    data-testid="shipment-modal-preview-body"
                    dangerouslySetInnerHTML={{ __html: preview.html }}
                  />
                  {preview.already_sent && (
                    <div className="text-xs text-emerald-800" data-testid="shipment-modal-already-sent">
                      This shipment email was already sent — resending is blocked to prevent duplicates.
                    </div>
                  )}
                  {sendState?.sent && (
                    <div className="text-xs text-emerald-800" data-testid="shipment-modal-sent-status">
                      Shipment email sent{sendState.sent_at ? ` at ${new Date(sendState.sent_at).toLocaleString()}` : ""}.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="p-5 border-t-2 border-[var(--ink)] flex items-center justify-between gap-2 flex-wrap">
          <button className="btn-ghost text-sm" onClick={onClose} data-testid="shipment-modal-close" disabled={busy}>
            Cancel
          </button>
          {step === "capture" ? (
            <button
              className="btn-red text-sm"
              onClick={handleSave}
              disabled={busy || (!isUsps && !tracking.trim())}
              data-testid="shipment-modal-save"
            >
              {busy ? "Saving…" : "Save Shipment & Preview Email"}
            </button>
          ) : (
            <button
              className="btn-red text-sm"
              onClick={handleSend}
              disabled={busy || !preview || preview?.already_sent || sendState?.sent}
              data-testid="shipment-modal-send"
            >
              {busy ? "Sending…"
                : (preview?.already_sent || sendState?.sent) ? "Email sent"
                : "Send Shipment Email"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Fulfillment Summary Card
// ─────────────────────────────────────────────────────────────────────
function FulfillmentSummaryCard({ summary, snap }) {
  const pct = summary.percent_complete ?? 100;
  const pctRounded = Math.round(pct);
  return (
    <div className="p-5 border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="summary-card">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest font-bold">Picking progress</div>
        <div className="font-display text-4xl leading-none text-[var(--panini-red)]"
              data-testid="progress-percent">
          {pctRounded}%
        </div>
      </div>
      {/* Phase 2 — Big visual progress bar per spec. */}
      <div className="mt-3">
        <div className="h-4 bg-neutral-200 border-2 border-[var(--ink)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--panini-red)] transition-all"
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                data-testid="progress-bar-fill" />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCell label="Ordered" value={summary.requested_total || 0} testid="sum-ordered" />
        <SummaryCell label="Picked"  value={summary.ready_total || 0}     testid="sum-picked"  tone="green" />
        <SummaryCell label="Pending" value={summary.pending_total || 0}   testid="sum-pending" tone="yellow" />
        <SummaryCell label="Total"   value={`$${(snap?.totals?.total || 0).toFixed(2)}`} testid="sum-total" tone="dark" />
      </div>
      {(summary.removed_total || 0) > 0 && (
        <div className="mt-2 text-xs text-[var(--smoke)]" data-testid="sum-removed-hint">
          {summary.removed_total} sticker{summary.removed_total === 1 ? "" : "s"} marked as removed.
        </div>
      )}
    </div>
  );
}

function SummaryCell({ label, value, tone, testid }) {
  const cls = {
    green: "bg-emerald-100 text-emerald-900 border-emerald-900",
    yellow: "bg-yellow-100 text-yellow-900 border-yellow-900",
    red: "bg-red-100 text-red-900 border-red-900",
    dark: "bg-[var(--ink)] text-[var(--panini-yellow)] border-[var(--ink)]",
  }[tone] || "bg-white text-[var(--ink)] border-[var(--ink)]";
  return (
    <div data-testid={testid} className={`p-3 rounded-lg border-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{label}</div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Customer Invoice (frozen)
//
//  Shows the administrator the complete customer invoice using the
//  pricing snapshot frozen at order placement. Never recalculates
//  amounts from today's price list — every number comes from
//  order.pricing_snapshot exactly as the customer saw it on checkout.
// ─────────────────────────────────────────────────────────────────────
function CustomerInvoiceCard({ order, snap, onSaved }) {
  const totals = snap?.totals || {};
  const lines = Array.isArray(snap?.lines) ? snap.lines : [];
  const cust = order?.customer || {};
  const delivery = order?.delivery || {};
  const addr = cust?.shipping_address || null;
  const tax = totals.tax || {};
  const shipping = totals.shipping || {};
  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const displayName = [cust.first_name, cust.last_name].filter(Boolean).join(" ")
    || cust.name || "—";
  const isPickup = (delivery.code || "").toLowerCase() === "local_pickup";
  const paid = Number(order?.payment_verification?.amount_paid || 0);
  const balanceDue = Math.max(0, Number(totals.total || 0) - paid);
  const ackTs = order?.usps_acknowledgment?.acknowledged_at;
  const [showChange, setShowChange] = React.useState(false);
  // Admin may run the change from any pre-shipped status. Preparing is
  // gated by the modal's own confirmation checkbox.
  const eligibleAdminChange = isPickup && [
    "awaiting_payment", "payment_submitted", "payment_verified", "preparing",
  ].includes(order?.status);

  return (
    <div
      className="p-5 border-2 border-[var(--ink)] rounded-2xl bg-white"
      data-testid="customer-invoice-card"
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
            Customer Invoice
          </div>
          <h3 className="mt-1 font-display text-xl md:text-2xl">
            Frozen at order placement
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]"
             data-testid="invoice-frozen-note">
          Prices frozen — never recalculated
        </div>
      </div>

      {/* Customer + order info */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div data-testid="invoice-customer-block">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Customer</div>
          <div className="mt-1 font-bold" data-testid="invoice-customer-name">{displayName}</div>
          <div data-testid="invoice-customer-email">{cust.email || "—"}</div>
          <div data-testid="invoice-customer-phone">{cust.phone || "—"}</div>
        </div>
        <div data-testid="invoice-order-block">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Order</div>
          <div className="mt-1" data-testid="invoice-order-id">
            <span className="font-bold">Order:</span> {order?.public_id || "—"}
          </div>
          {/* Feb-2026 · Invoice variant. Reads from persisted resolver
              output only. Never inferred. */}
          {order?.variant_resolution && (
            <div className="mt-1" data-testid="invoice-variant">
              <span className="font-bold">Variant:</span>{" "}
              <VariantBadge
                variant={order.variant_resolution.effective_variant}
                conflict={order.variant_resolution.variant_conflict}
                size="md"
              />
            </div>
          )}
          {order?.source_quote_public_id ? (
            <div data-testid="invoice-quote-id">
              <span className="font-bold">Original Quote:</span>{" "}
              <Link
                to={`/admin/quotes/${order.source_quote_public_id}`}
                className="underline decoration-2 underline-offset-2 font-mono font-bold text-[var(--ink)] hover:opacity-70"
                data-testid="invoice-quote-link"
              >
                {order.source_quote_public_id}
              </Link>
            </div>
          ) : (
            <div className="text-[11px] text-[var(--smoke)]" data-testid="invoice-order-origin">
              <span className="font-bold">Origin:</span>{" "}
              {order?.manual_entry
                ? "Manual admin order"
                : (order?.checkout_type === "guest"
                    ? "Direct guest website order"
                    : (order?.checkout_type === "account"
                        ? "Direct website order"
                        : "Direct website order"))}
            </div>
          )}
          <div data-testid="invoice-order-date">
            <span className="font-bold">Placed:</span>{" "}
            {order?.created_at ? new Date(order.created_at).toLocaleString() : "—"}
          </div>
          <div data-testid="invoice-payment-status">
            <span className="font-bold">Payment:</span>{" "}
            {order?.payment_verification?.status || order?.status || "—"}
          </div>
        </div>
        <div className="md:col-span-2" data-testid="invoice-delivery-block">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
            Delivery
          </div>
          <div className="mt-1">
            <span className="font-bold" data-testid="invoice-delivery-label">
              {delivery.label || delivery.code || "—"}
            </span>
            {" · "}
            <span data-testid="invoice-delivery-cost">
              {Number(shipping.cost || 0) === 0 ? "Free" : fmt(shipping.cost)}
            </span>
          </div>
          {isPickup ? (
            <div className="mt-1 text-xs text-[var(--smoke)]">
              Your Phone Plug · 20719 NW 2nd Ave, Miami, FL 33160
            </div>
          ) : addr ? (
            <div className="mt-1 text-xs text-[var(--smoke)]" data-testid="invoice-shipping-address">
              {addr.line1}
              {addr.line2 ? `, ${addr.line2}` : ""}
              {`, ${addr.city}, ${addr.state} ${addr.postal_code}`}
              {addr.country ? `, ${addr.country}` : ""}
            </div>
          ) : null}
          {ackTs && (
            <div className="mt-1 text-[11px] italic text-[var(--smoke)]"
                 data-testid="invoice-usps-ack">
              USPS Stamp no-tracking risk acknowledged at{" "}
              {new Date(ackTs).toLocaleString()}
            </div>
          )}
          {eligibleAdminChange && (
            <button
              type="button" onClick={() => setShowChange(true)}
              className="btn bg-[var(--panini-red)] text-white text-xs mt-3"
              data-testid="admin-cta-change-delivery"
            >
              Change Delivery Method
            </button>
          )}
        </div>
      </div>

      {showChange && (
        <AdminChangeDeliveryModal
          open={showChange}
          onClose={() => setShowChange(false)}
          order={order}
          onSaved={onSaved}
        />
      )}

      {/* Sticker lines */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
          Sticker Lines
        </div>
        {lines.length === 0 ? (
          <div className="mt-2 text-xs text-[var(--smoke)] italic">
            No priced sticker lines on this order.
          </div>
        ) : (
          <div
            className="mt-2 overflow-x-auto rounded-xl border-2 border-[var(--ink)]"
            data-testid="invoice-lines"
          >
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--panini-yellow-soft)]">
                <tr className="text-left">
                  <th className="px-3 py-2 font-bold">Code</th>
                  <th className="px-3 py-2 font-bold">Player / Name</th>
                  <th className="px-3 py-2 font-bold text-right">Qty</th>
                  <th className="px-3 py-2 font-bold text-right">Unit</th>
                  <th className="px-3 py-2 font-bold text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr
                    key={`${l.code}-${idx}`}
                    className="border-t border-[var(--stone)]"
                    data-testid={`invoice-line-${l.code}`}
                  >
                    <td className="px-3 py-2 font-mono">{l.code}</td>
                    <td className="px-3 py-2">{l.name || l.player_name || l.title || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono" data-testid={`invoice-line-${l.code}-qty`}>
                      {l.qty}
                    </td>
                    <td className="px-3 py-2 text-right font-mono" data-testid={`invoice-line-${l.code}-unit`}>
                      {fmt(l.unit_price)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold" data-testid={`invoice-line-${l.code}-total`}>
                      {fmt(l.subtotal ?? Number(l.qty || 0) * Number(l.unit_price || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Financial summary */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div />
        <div className="rounded-xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-4 space-y-1.5">
          <InvoiceRow label="Sticker subtotal" value={fmt(totals.subtotal)} testid="invoice-subtotal" />
          <InvoiceRow
            label={`Sales tax (${((tax.rate || 0) * 100).toFixed(2)}%)`}
            value={fmt(tax.amount)}
            testid="invoice-tax"
          />
          <InvoiceRow
            label={`Shipping — ${shipping.label || delivery.label || delivery.code || "—"}`}
            value={Number(shipping.cost || 0) === 0 ? "Free" : fmt(shipping.cost)}
            testid="invoice-shipping"
          />
          <div className="pt-2 mt-2 border-t-2 border-[var(--ink)] flex items-center justify-between">
            <span className="font-bold text-base">Final total</span>
            <span
              className="font-display text-2xl"
              data-testid="invoice-total"
            >
              {fmt(totals.total)}
            </span>
          </div>
          {paid > 0 && (
            <>
              <InvoiceRow label="Amount paid" value={fmt(paid)} testid="invoice-paid" />
              <InvoiceRow
                label="Balance due"
                value={fmt(balanceDue)}
                testid="invoice-balance-due"
                tone={balanceDue > 0 ? "red" : "green"}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoiceRow({ label, value, testid, tone }) {
  const cls =
    tone === "red"
      ? "text-red-800"
      : tone === "green"
      ? "text-emerald-900"
      : "";
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testid}>
      <span className={`text-xs ${cls}`}>{label}</span>
      <span className={`font-mono font-bold ${cls}`}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  List editor (waiting / removed) — keyboard-first
// ─────────────────────────────────────────────────────────────────────
function ListEditorCard({ title, subtitle, list, items, otherItems, requestedByCode, canEdit, publicId, onSaved, pushUndo, testid }) {
  const [local, setLocal] = useState(items);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newQty, setNewQty] = useState(1);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const codeInputRef = useRef(null);

  useEffect(() => { setLocal(items); setDirty(false); }, [items]);

  const otherByCode = useMemo(() => Object.fromEntries(otherItems.map((i) => [i.code, i.qty])), [otherItems]);

  const capFor = (code) => (requestedByCode[code] || 0) - (otherByCode[code] || 0);

  const addRow = (code, qty) => {
    const c = (code || "").trim().toUpperCase();
    const q = Math.max(1, parseInt(qty || 1, 10));
    if (!c) return toast.error("Sticker code is required.");
    if (!(c in requestedByCode)) return toast.error(`${c} is not part of this order.`);
    setLocal((prev) => {
      const existing = prev.find((i) => i.code === c);
      let next;
      if (existing) {
        next = prev.map((i) => i.code === c ? { ...i, qty: i.qty + q } : i);
      } else {
        next = [...prev, { code: c, qty: q }];
      }
      const cap = capFor(c);
      const total = next.find((i) => i.code === c).qty;
      if (total > cap) {
        toast.error(`${c}: total ${total} exceeds cap ${cap}.`);
        return prev;
      }
      setDirty(true);
      return next;
    });
    setNewCode(""); setNewQty(1);
    codeInputRef.current?.focus();
  };

  const removeRow = (code) => {
    setLocal((prev) => prev.filter((i) => i.code !== code));
    setDirty(true);
  };

  const changeQty = (code, qty) => {
    const q = Math.max(1, parseInt(qty || 1, 10));
    const cap = capFor(code);
    if (q > cap) { toast.error(`${code}: max ${cap}`); return; }
    setLocal((prev) => prev.map((i) => i.code === code ? { ...i, qty: q } : i));
    setDirty(true);
  };

  const changeReason = (code, reason) => {
    setLocal((prev) => prev.map((i) => i.code === code ? { ...i, reason } : i));
    setDirty(true);
  };

  const bulkPaste = () => {
    // Accept: "CODE", "CODE QTY", "CODE, QTY", one per line or comma-separated.
    const tokens = bulkText.split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean);
    let addedCount = 0;
    const draft = [...local];
    for (const tk of tokens) {
      const parts = tk.split(/\s+/);
      const code = (parts[0] || "").toUpperCase();
      const qty = Math.max(1, parseInt(parts[1] || "1", 10));
      if (!code) continue;
      if (!(code in requestedByCode)) { toast.error(`${code} skipped (not in order).`); continue; }
      const idx = draft.findIndex((i) => i.code === code);
      const cap = (requestedByCode[code] || 0) - (otherByCode[code] || 0);
      if (idx >= 0) {
        const newQ = draft[idx].qty + qty;
        if (newQ > cap) { toast.error(`${code} skipped (exceeds cap ${cap}).`); continue; }
        draft[idx] = { ...draft[idx], qty: newQ };
      } else {
        if (qty > cap) { toast.error(`${code} skipped (exceeds cap ${cap}).`); continue; }
        draft.push({ code, qty });
      }
      addedCount++;
    }
    setLocal(draft);
    setDirty(true);
    setShowBulk(false); setBulkText("");
    toast.success(`Bulk paste added/merged ${addedCount} code${addedCount === 1 ? "" : "s"}.`);
  };

  const save = async () => {
    setBusy(true);
    const prev = items;
    try {
      const path = list === "pending" ? "waiting-list" : "removed-list";
      await api.patch(`/admin/orders/${publicId}/${path}`, { items: local });
      toast.success(`${title} saved.`);
      pushUndo({
        label: `${title} updated`,
        revert: () => api.patch(`/admin/orders/${publicId}/${path}`, { items: prev }),
      });
      setDirty(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const copyList = async () => {
    const text = local.map((i) => `${i.code}${i.qty > 1 ? ` x${i.qty}` : ""}`).join("\n");
    if (!text) return toast.info("Nothing to copy.");
    await navigator.clipboard.writeText(text);
    toast.success(`${title} copied to clipboard.`);
  };

  const total = local.reduce((s, i) => s + i.qty, 0);

  return (
    <div className={`border-2 rounded-2xl bg-white overflow-hidden ${list === "pending" ? "border-[var(--panini-red)] shadow-[6px_6px_0_var(--panini-red)]" : "border-[var(--ink)]"}`} data-testid={`${testid}-card`}>
      <div className="flex items-center justify-between p-4 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-bold ${list === "pending" ? "text-[var(--panini-red)]" : ""}`}>{list === "pending" ? "Primary workspace" : "Secondary"}</div>
          <h3 className="font-display text-2xl">{title} · <span className="opacity-60 text-lg">{total}</span></h3>
          <p className="text-xs opacity-70">{subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copyList} className="btn-ghost text-xs" data-testid={`${testid}-copy`}>
            <Copy size={12} /> Copy list
          </button>
          {canEdit && (
            <button onClick={() => setShowBulk(true)} className="btn-ghost text-xs" data-testid={`${testid}-bulk-open`}>
              <ClipboardPaste size={12} /> Bulk paste
            </button>
          )}
          {canEdit && dirty && (
            <button onClick={save} disabled={busy} className="btn-red text-xs" data-testid={`${testid}-save`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : null} Save
            </button>
          )}
        </div>
      </div>

      {/* Add row (keyboard-first) */}
      {canEdit && (
        <form
          className="flex flex-wrap gap-2 items-end p-4 border-b border-[var(--stone)] bg-[var(--paper)]/40"
          onSubmit={(e) => { e.preventDefault(); addRow(newCode, newQty); }}
          data-testid={`${testid}-add-row`}
        >
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest font-bold">Sticker code</span>
            <input
              ref={codeInputRef}
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-sm font-mono w-36"
              placeholder="e.g. FWC42"
              autoFocus
              data-testid={`${testid}-new-code`}
            />
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest font-bold">Qty</span>
            <input
              type="number" min="1" value={newQty} onChange={(e) => setNewQty(e.target.value)}
              className="border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-sm w-20"
              data-testid={`${testid}-new-qty`}
            />
          </label>
          <button type="submit" className="btn-yellow text-sm" data-testid={`${testid}-add-btn`}><Plus size={14} /> Add</button>
          <span className="ml-3 text-xs opacity-60">Enter to add · Tab to move fields</span>
        </form>
      )}

      {/* Rows */}
      <div className="p-4 space-y-2">
        {local.length === 0 && <div className="text-sm opacity-60 py-4 text-center" data-testid={`${testid}-empty`}>Nothing here yet.</div>}
        {local.map((it, idx) => (
          <div key={`${it.code}-${idx}`} className="flex flex-wrap items-center gap-2 p-2 border-2 border-[var(--ink)] rounded-lg bg-white" data-testid={`${testid}-row-${it.code}`}>
            <div className="flex flex-col min-w-[7rem]">
              <span className="font-mono font-bold">{it.code}</span>
              {(it.display_name || it.player_name || it.subject) && (
                <span className="text-[11px] text-[var(--smoke)] leading-tight" data-testid={`${testid}-name-${it.code}`}>
                  {it.display_name || it.player_name || it.subject}
                </span>
              )}
            </div>
            {canEdit ? (
              <input
                type="number" min="1"
                value={it.qty}
                onChange={(e) => changeQty(it.code, e.target.value)}
                className="border-2 border-[var(--ink)] rounded px-2 py-1 text-sm w-20"
                data-testid={`${testid}-qty-${it.code}`}
              />
            ) : <span className="text-sm">Qty {it.qty}</span>}
            <span className="text-xs opacity-60">of {requestedByCode[it.code]} requested</span>
            {list === "removed" && (
              canEdit ? (
                <input
                  type="text"
                  value={it.reason || ""}
                  onChange={(e) => changeReason(it.code, e.target.value)}
                  placeholder="Reason (optional)"
                  className="flex-1 border-2 border-[var(--ink)] rounded px-2 py-1 text-sm"
                  data-testid={`${testid}-reason-${it.code}`}
                />
              ) : it.reason && <span className="text-xs italic opacity-70">{it.reason}</span>
            )}
            {canEdit && (
              <button
                onClick={() => removeRow(it.code)}
                className="ml-auto text-red-800 hover:bg-red-50 rounded p-1"
                data-testid={`${testid}-remove-${it.code}`}
                title="Remove from list"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Bulk paste modal */}
      {showBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid={`${testid}-bulk-modal`}>
          <div className="w-full max-w-lg bg-white border-2 border-[var(--ink)] rounded-2xl p-6">
            <h3 className="font-display text-xl">Bulk paste into {title}</h3>
            <p className="text-xs opacity-70 mt-1">One entry per line or comma-separated. Format: <code>CODE</code> or <code>CODE QTY</code>.</p>
            <textarea
              className="w-full mt-3 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm font-mono"
              rows={8}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              autoFocus
              data-testid={`${testid}-bulk-text`}
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button className="btn-ghost text-sm" onClick={() => { setShowBulk(false); setBulkText(""); }} data-testid={`${testid}-bulk-cancel`}>Cancel</button>
              <button className="btn-yellow text-sm" onClick={bulkPaste} data-testid={`${testid}-bulk-apply`}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Requested reference (read-only)
// ─────────────────────────────────────────────────────────────────────
function RequestedReferenceCard({ order }) {
  const items = order.requested_items || [];
  const total = items.reduce((s, i) => s + i.qty, 0);
  const copy = async () => {
    const text = items.map((i) => `${i.code}${i.qty > 1 ? ` x${i.qty}` : ""}`).join("\n");
    await navigator.clipboard.writeText(text);
    toast.success("Requested list copied.");
  };
  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="requested-reference-card">
      <div className="flex items-center justify-between p-4 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold">Reference · read only</div>
          <h3 className="font-display text-2xl">Requested list · <span className="opacity-60 text-lg">{total}</span></h3>
        </div>
        <button onClick={copy} className="btn-ghost text-xs" data-testid="requested-copy"><Copy size={12} /> Copy</button>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
        {items.map((i, idx) => (
          <div key={`req-${i.code}-${idx}`} className="flex items-center justify-between gap-2 border border-[var(--stone)] rounded px-2 py-1">
            <div className="flex flex-col min-w-0">
              <span className="font-mono">{i.code}</span>
              {(i.display_name || i.player_name || i.subject) && (
                <span className="text-[10px] text-[var(--smoke)] truncate" data-testid={`requested-name-${i.code}`}>
                  {i.display_name || i.player_name || i.subject}
                </span>
              )}
            </div>
            <span className="opacity-60 shrink-0">×{i.qty}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Events log
// ─────────────────────────────────────────────────────────────────────
function EventsCard({ events }) {
  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="events-card">
      <div className="p-4 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
        <div className="text-[10px] uppercase tracking-widest font-bold">Audit trail</div>
        <h3 className="font-display text-lg">Recent activity</h3>
      </div>
      <ul className="divide-y divide-[var(--stone)]">
        {events.length === 0 && <li className="p-4 text-sm opacity-60">No events yet.</li>}
        {events.map((e, i) => (
          <li key={i} className="p-3 text-sm flex items-center justify-between gap-2" data-testid={`event-${e.type}-${i}`}>
            <span className="font-mono text-xs opacity-70">{new Date(e.at).toLocaleString()}</span>
            <span className="flex-1 mx-3"><b>{e.type.replaceAll("_", " ")}</b> · {e.actor}{e.actor_id ? ` (${e.actor_id})` : ""}</span>
            {e.payload?.to && <span className="text-xs opacity-60">→ {e.payload.to}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Phase 1b — WhatsApp deep-link button (dynamic per delivery method)
// ─────────────────────────────────────────────────────────────────────
function WhatsAppMsgBtn({ order, kind = "ready" }) {
  const [loading, setLoading] = useState(false);
  const openWa = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/orders/${order.public_id}/whatsapp-message`, {
        params: { kind },
      });
      if (r.data?.wa_url) window.open(r.data.wa_url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not build WhatsApp link.");
    } finally { setLoading(false); }
  };
  return (
    <button onClick={openWa} disabled={loading} className="btn-ghost text-sm" data-testid="btn-whatsapp-pickup">
      {loading ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} WhatsApp customer
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Phase 1b — Picking Checklist Card (primary workflow)
// ─────────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { code: "Pending", label: "Pending", tone: "gray" },
  { code: "PartiallyPicked", label: "Partial", tone: "yellow" },
  { code: "Picked", label: "Picked", tone: "green" },
  { code: "OutOfStock", label: "Out of stock", tone: "red" },
  { code: "Removed", label: "Removed", tone: "red" },
];

const STATUS_TONE = {
  Pending: "bg-neutral-100 text-neutral-800",
  PartiallyPicked: "bg-yellow-100 text-yellow-900",
  Picked: "bg-green-100 text-green-900",
  OutOfStock: "bg-red-100 text-red-900",
  Removed: "bg-red-100 text-red-900 line-through",
};

function PickingChecklistCard({ order, onSaved, canEdit, correctMode, onExitCorrect }) {
  // Local editable copy — user picks freely, then presses Save.
  const initial = useMemo(
    () => (order.fulfillment_items || []).map((it) => ({ ...it })),
    [order.fulfillment_items]
  );
  const [items, setItems] = useState(initial);
  const [dirtyMap, setDirtyMap] = useState({});
  // Phase 2 — After Save Progress succeeds, open the fulfillment update
  // modal so the picker can choose whether to notify the customer.
  const [postSaveNotifyOpen, setPostSaveNotifyOpen] = useState(false);
  // Phase 3A.1 — Intermediate "Notify customer?" prompt. Save Progress
  // first opens this small confirmation modal; the admin then chooses
  // "Not Now" or "Preview Customer Email" which opens the full modal.
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [lastSaveSummary, setLastSaveSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("album");
  const [saving, setSaving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef({});

  useEffect(() => {
    setItems(initial);
    setDirtyMap({});
  }, [initial]);

  const filteredIndexes = useMemo(() => {
    const q = search.trim().toUpperCase();
    let idxs = items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => {
        if (q && !(it.code || "").toUpperCase().includes(q)) return false;
        if (statusFilter !== "all" && it.status !== statusFilter) return false;
        return true;
      });
    // Phase 2 — Deterministic sort helpers using the shared canonical
    // album order (`@/lib/albumOrder`). Same module as the picking
    // list PDF, invoice grouping, and backend `album_sort_key` — so
    // the picker always sees rows in the EXACT physical page-flip
    // sequence of the printed album.
    const albumKey = (code) => albumSortKey(code);
    const cmp = (a, b) => {
      for (let i = 0; i < Math.max(a.length, b.length); i++) {
        const av = a[i], bv = b[i];
        if (av === bv) continue;
        if (av === undefined) return -1;
        if (bv === undefined) return 1;
        if (typeof av === "number" && typeof bv === "number") return av - bv;
        return String(av).localeCompare(String(bv));
      }
      return 0;
    };
    if (sortBy === "album") {
      idxs.sort((a, b) => cmp(albumKey(a.it.code), albumKey(b.it.code)));
    } else if (sortBy === "code") {
      idxs.sort((a, b) => a.it.code.localeCompare(b.it.code, "en", { numeric: true }));
    } else if (sortBy === "country") {
      // Group by country prefix alphabetically, keep numeric within.
      idxs.sort((a, b) => {
        const [pa, na] = parseCode(a.it.code);
        const [pb, nb] = parseCode(b.it.code);
        if (pa && pb && pa !== pb) return pa.localeCompare(pb);
        if (!pa || !pb) return (a.it.code || "").localeCompare(b.it.code || "");
        return (na || 0) - (nb || 0);
      });
    } else if (sortBy === "number") {
      // Sticker-number-only, prefix ignored for micro-batch picking within a page.
      idxs.sort((a, b) => {
        const [, na] = parseCode(a.it.code);
        const [, nb] = parseCode(b.it.code);
        return (na || 0) - (nb || 0);
      });
    } else if (sortBy === "status") {
      idxs.sort((a, b) => (a.it.status || "").localeCompare(b.it.status || ""));
    }
    return idxs.map((x) => x.i);
  }, [items, search, statusFilter, sortBy]);

  const dirtyCount = Object.keys(dirtyMap).length;
  const anyDirty = dirtyCount > 0;

  const markDirty = (code) => setDirtyMap((m) => ({ ...m, [code]: true }));

  const updateItem = (code, patch) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.code !== code) return it;
        const merged = { ...it, ...patch };
        const req = Number(it.qty_requested || 0);
        // Coerce qty_picked & derive status when the caller adjusts picked
        if (patch.qty_picked !== undefined) {
          const picked = Math.max(0, Math.min(req, Number(patch.qty_picked) || 0));
          merged.qty_picked = picked;
          merged.qty_remaining = req - picked;
          if (patch.status === undefined) {
            merged.status = picked === 0 ? "Pending" : picked === req ? "Picked" : "PartiallyPicked";
          }
        }
        if (patch.status !== undefined) {
          if (patch.status === "Picked") { merged.qty_picked = req; merged.qty_remaining = 0; }
          else if (patch.status === "Pending") { merged.qty_picked = 0; merged.qty_remaining = req; }
          else if (patch.status === "Removed") { merged.qty_picked = 0; merged.qty_remaining = 0; }
          else if (patch.status === "OutOfStock") {
            merged.qty_remaining = Math.max(0, req - Number(merged.qty_picked || 0));
          }
        }
        return merged;
      })
    );
    markDirty(code);
  };

  const toggleChecked = (code) => {
    const it = items.find((x) => x.code === code);
    if (!it) return;
    const req = Number(it.qty_requested || 0);
    if (req > 1) {
      // For duplicate qty >1 the checkbox toggles between fully-picked and empty
      updateItem(code, { status: it.qty_picked === req ? "Pending" : "Picked" });
    } else {
      updateItem(code, { status: it.status === "Picked" ? "Pending" : "Picked" });
    }
  };

  const incQty = (code, delta) => {
    const it = items.find((x) => x.code === code);
    if (!it) return;
    const next = Math.max(0, Math.min(Number(it.qty_requested || 0), Number(it.qty_picked || 0) + delta));
    updateItem(code, { qty_picked: next });
  };

  // Per-Unit Picking — toggle exactly one physical unit of an item.
  // Independent of siblings: checking unit 3 does not affect units 1/2/4/5.
  // Uses idempotent local state + a `unit_index/unit_picked` patch that
  // the backend translates into a bitmap update.
  const toggleUnit = (code, unitIndex) => {
    setItems((prev) => prev.map((it) => {
      if (it.code !== code) return it;
      const req = Number(it.qty_requested || 0);
      const cur = Array.isArray(it.unit_picked) && it.unit_picked.length === req
        ? it.unit_picked.slice()
        : Array.from({ length: req }, (_, i) => i < Number(it.qty_picked || 0));
      cur[unitIndex] = !cur[unitIndex];
      const picked = cur.filter(Boolean).length;
      const status = picked === 0 ? "Pending"
                    : picked === req ? "Picked" : "PartiallyPicked";
      return { ...it, unit_picked: cur, qty_picked: picked,
                qty_remaining: req - picked, status };
    }));
    setDirtyMap((m) => ({ ...m, [code]: true }));
  };

  const save = async () => {
    if (!anyDirty) return;
    setSaving(true);
    try {
      const payload = {
        items: items
          .filter((it) => dirtyMap[it.code])
          .map((it) => ({
            code: it.code,
            qty_picked: Number(it.qty_picked || 0),
            unit_picked: Array.isArray(it.unit_picked) ? it.unit_picked : undefined,
            status: it.status,
            note: it.note || "",
          })),
      };
      const endpoint = correctMode
        ? `/admin/orders/${order.public_id}/fulfillment/correct`
        : `/admin/orders/${order.public_id}/fulfillment`;
      const method = correctMode ? "post" : "patch";
      const r = await api[method](endpoint, payload);
      toast.success(
        correctMode
          ? `Corrected ${payload.items.length} sticker${payload.items.length === 1 ? "" : "s"}. Order status unchanged.`
          : `Saved ${payload.items.length} sticker${payload.items.length === 1 ? "" : "s"}.`
      );
      setDirtyMap({});
      if (correctMode && typeof onExitCorrect === "function") onExitCorrect();
      // Phase 3A.1 — Open the intermediate "Notify customer?" prompt.
      // The full email preview only appears if the admin clicks
      // "Preview Customer Email" on this prompt.
      // Phase 1-ops · Skip the notify prompt entirely in correction mode
      // (Safeguard 1: correction must never trigger a duplicate email).
      if (!correctMode) {
        setLastSaveSummary({ count: payload.items.length });
        setSavePromptOpen(true);
      }
      if (r?.data) onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  // Keyboard shortcuts — SPACE toggles active row, UP/DOWN move, ENTER = save.
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (e.key === " ") {
        e.preventDefault();
        const idx = filteredIndexes[activeIndex];
        if (idx !== undefined) toggleChecked(items[idx].code);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(filteredIndexes.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          save();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filteredIndexes, activeIndex, items, canEdit]);

  useEffect(() => {
    const idx = filteredIndexes[activeIndex];
    if (idx !== undefined && rowRefs.current[idx]) {
      rowRefs.current[idx].scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, filteredIndexes]);

  if (!(order.fulfillment_items || []).length) {
    return (
      <div className="p-5 border-2 border-[var(--ink)] rounded-2xl bg-white text-sm text-[var(--smoke)]"
           data-testid="picking-checklist-empty">
        The picking checklist appears here after payment verification.
      </div>
    );
  }

  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="picking-checklist">
      <div className="p-5 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-xl">Picking checklist</h3>
            <p className="text-xs text-[var(--smoke)] mt-1">
              Confirm every sticker manually. SPACE toggles picked · ↑/↓ moves · Ctrl+Enter saves.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Phase 1d §3 — Select-All / Unselect-All bulk controls.
                Every visual checkbox derives from `unit_picked` (see the
                unit fallback below where `units = it.unit_picked ?? …`),
                so bulk toggles MUST refresh `unit_picked` on every item
                or the checkbox render will keep whatever state a prior
                individual `toggleUnit` left behind. Business rules,
                statuses, and quantities are untouched — this is a
                render-sync fix only. */}
            <button
              type="button"
              onClick={() => {
                setItems((prev) => prev.map((it) => {
                  const req = Number(it.qty_requested || 0);
                  return {
                    ...it,
                    qty_picked: req,
                    qty_remaining: 0,
                    status: "Picked",
                    unit_picked: Array.from({ length: req }, () => true),
                  };
                }));
                setDirtyMap(Object.fromEntries((items || []).map((it) => [it.code, true])));
              }}
              disabled={!canEdit || saving}
              className="btn-ghost text-sm border-2 border-[var(--ink)]"
              data-testid="btn-select-all"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => {
                setItems((prev) => prev.map((it) => {
                  const req = Number(it.qty_requested || 0);
                  return {
                    ...it,
                    qty_picked: 0,
                    qty_remaining: req,
                    status: "Pending",
                    unit_picked: Array.from({ length: req }, () => false),
                  };
                }));
                setDirtyMap(Object.fromEntries((items || []).map((it) => [it.code, true])));
              }}
              disabled={!canEdit || saving}
              className="btn-ghost text-sm border-2 border-[var(--ink)]"
              data-testid="btn-unselect-all"
            >
              Unselect all
            </button>
            <button
              onClick={save}
              disabled={!anyDirty || saving || !canEdit}
              className="btn-yellow text-sm"
              data-testid="btn-picking-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save progress{anyDirty ? ` (${dirtyCount})` : ""}
            </button>
          </div>
        </div>

        {/* Search / filters / sort */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 opacity-60" />
            <input
              className="w-full border-2 border-[var(--ink)] rounded-lg pl-7 pr-2 py-1.5 text-sm bg-white"
              placeholder="Search sticker code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="picking-search"
            />
          </div>
          <select
            className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="picking-status-filter"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
          <select
            className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="picking-sort"
          >
            <option value="album">Album order (default)</option>
            <option value="code">Alphabetical</option>
            <option value="country">Country</option>
            <option value="number">Sticker number</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="picking-table">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-right px-3 py-2">Req</th>
              <th className="text-center px-3 py-2 w-28">Picked</th>
              <th className="text-right px-3 py-2">Left</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {filteredIndexes.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-[var(--smoke)]" data-testid="picking-empty-filter">
                No stickers match the current filter.
              </td></tr>
            )}
            {filteredIndexes.flatMap((idx, activeRow) => {
              const it = items[idx];
              const req = Math.max(1, Number(it.qty_requested || 0));
              const units = Array.isArray(it.unit_picked) && it.unit_picked.length === req
                ? it.unit_picked
                : Array.from({ length: req }, (_, i) => i < Number(it.qty_picked || 0));
              const totalPickedOnItem = units.filter(Boolean).length;
              const isActive = activeRow === activeIndex;
              const isDirty = !!dirtyMap[it.code];
              return units.map((isChecked, unitIndex) => (
                <tr
                  key={`fi-${it.code}-${idx}-u${unitIndex}`}
                  ref={unitIndex === 0 ? (el) => { rowRefs.current[idx] = el; } : undefined}
                  onClick={() => setActiveIndex(activeRow)}
                  className={
                    "border-t border-neutral-200 " +
                    (isActive ? "bg-yellow-50 " : "") +
                    (isDirty ? "ring-1 ring-yellow-400 " : "")
                  }
                  data-testid={`picking-row-${it.code}-u${unitIndex}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-black"
                      checked={!!isChecked}
                      onChange={() => toggleUnit(it.code, unitIndex)}
                      disabled={!canEdit}
                      data-testid={`picking-check-${it.code}-u${unitIndex}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono font-bold">
                    <div className="flex items-center gap-1.5">
                      <span>{it.code}</span>
                      {/* P3.2.1 · Variant is a PHYSICAL picking
                          instruction and must remain visible on
                          every physical unit row (not just u0).
                          White intentionally shows nothing to keep
                          the picking view clean. */}
                      {it.variant && it.variant !== "white" && (
                        <PickingVariantChip variant={it.variant} />
                      )}
                    </div>
                    {(it.display_name || it.player_name || it.subject) && (
                      <div className="text-[10px] font-sans font-normal text-[var(--smoke)] leading-tight max-w-[14ch] truncate" title={it.display_name || it.player_name || it.subject} data-testid={`picking-name-${it.code}`}>
                        {it.display_name || it.player_name || it.subject}
                      </div>
                    )}
                    {req > 1 && (
                      <div className="text-[10px] font-sans font-normal text-[var(--panini-red)] leading-tight" data-testid={`picking-unit-label-${it.code}-u${unitIndex}`}>
                        {unitIndex + 1} of {req}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[var(--smoke)]">
                    {req > 1 && unitIndex > 0 ? "" : (it.country || "—")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {req > 1 && unitIndex > 0 ? "" : req}
                  </td>
                  <td className="px-3 py-2 text-center font-mono">
                    {unitIndex === 0 ? `${totalPickedOnItem} / ${req}` : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {req > 1 && unitIndex > 0 ? "" : Number(it.qty_remaining || 0)}
                  </td>
                  <td className="px-3 py-2">
                    {req > 1 && unitIndex > 0 ? null : (
                      <select
                        className={"border rounded px-1.5 py-0.5 text-xs " + (STATUS_TONE[it.status] || "")}
                        value={it.status || "Pending"}
                        onChange={(e) => updateItem(it.code, { status: e.target.value })}
                        disabled={!canEdit}
                        data-testid={`picking-status-${it.code}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.code} value={s.code}>{s.label}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {req > 1 && unitIndex > 0 ? null : (
                      <input
                        type="text"
                        className="w-full border rounded px-1.5 py-0.5 text-xs"
                        placeholder="internal note"
                        value={it.note || ""}
                        onChange={(e) => updateItem(it.code, { note: e.target.value })}
                        disabled={!canEdit}
                        maxLength={500}
                        data-testid={`picking-note-${it.code}`}
                      />
                    )}
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>

      {/* Phase 3A.1 — Intermediate confirmation modal shown right after
          Save Progress. Admin chooses Not Now (dismiss) or Preview
          Customer Email (opens the full fulfillment update modal). */}
      {savePromptOpen && (
        <SaveProgressPromptModal
          count={lastSaveSummary?.count || 0}
          onClose={() => setSavePromptOpen(false)}
          onPreview={() => { setSavePromptOpen(false); setPostSaveNotifyOpen(true); }}
        />
      )}
      {/* Phase 2 — Post-save notify-customer modal */}
      {postSaveNotifyOpen && (
        <NotifyCustomerModal
          publicId={order.public_id}
          onClose={() => setPostSaveNotifyOpen(false)}
          onSent={() => onSaved?.()}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Phase 1b §14 — Archive & Delete Test Order
// ─────────────────────────────────────────────────────────────────────
function DangerZoneCard({ order, onDone }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const isArchived = !!order.archived;

  const toggleArchive = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.public_id}/archive`, { archived: !isArchived });
      toast.success(isArchived ? "Order restored." : "Order archived.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Archive failed.");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (confirmText !== "DELETE") return;
    setBusy(true);
    try {
      await api.delete(`/admin/orders/${order.public_id}`, { data: { confirm: "DELETE" } });
      toast.success("Test order deleted.");
      nav("/admin?tab=fulfillment");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-red-800 rounded-2xl bg-red-50 p-5" data-testid="danger-zone">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={16} className="text-red-800" />
        <h3 className="font-display text-lg text-red-900">Danger zone</h3>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={toggleArchive}
          disabled={busy}
          className="btn-ghost text-sm border-2 border-red-800 text-red-900"
          data-testid="btn-archive-order"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ArchiveIcon size={14} />}
          {isArchived ? "Restore order" : "Archive order"}
        </button>
        <button
          onClick={() => { setConfirmText(""); setShowDelete(true); }}
          disabled={busy}
          className="btn-red text-sm"
          data-testid="btn-delete-test-order"
        >
          <Trash2 size={14} /> Delete test order
        </button>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="delete-modal">
          <div className="w-full max-w-md bg-white border-2 border-red-800 rounded-2xl p-6 shadow-[8px_8px_0_var(--ink)]">
            <div className="flex items-center gap-2 text-red-900 mb-2">
              <AlertTriangle size={18} />
              <h3 className="font-display text-xl">Permanently delete {order.public_id}?</h3>
            </div>
            <p className="text-sm text-red-900">
              This will remove the order, its history, and its picking state from
              the database. There is no undo. Use this only for test orders.
            </p>
            <p className="text-sm mt-3">Type <code className="font-mono bg-neutral-100 px-1">DELETE</code> to confirm:</p>
            <input
              type="text"
              className="w-full mt-2 border-2 border-red-800 rounded-lg px-3 py-2 text-sm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
              autoFocus
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button className="btn-ghost text-sm" onClick={() => setShowDelete(false)} data-testid="delete-cancel">
                Nevermind
              </button>
              <button
                className="btn-red text-sm"
                onClick={doDelete}
                disabled={busy || confirmText !== "DELETE"}
                data-testid="delete-confirm"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Permanently delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Phase 2 · Payment Confirmation Modal
// ─────────────────────────────────────────────────────────────
function PaymentConfirmationModal({ order, onCancel, onConfirm }) {
  const expected = Number(order?.pricing_snapshot?.totals?.total || 0);
  const balanceDueRemaining = Math.max(0, expected - Number(order?.payment?.total_received_to_date || 0));
  const defaultAmount = balanceDueRemaining > 0 ? balanceDueRemaining : expected;
  const originalMethod = String(order?.payment?.method || order?.payment?.payment_method_original || "").toLowerCase() || "";

  const [step, setStep]                   = useState(1);
  const [amountReceived, setAmountReceived] = useState(defaultAmount.toFixed(2));
  const [method, setMethod]                 = useState(originalMethod || "zelle");
  const [referenceId, setReferenceId]       = useState("");
  const [zelleSender, setZelleSender]       = useState("");
  const [receivedAt, setReceivedAt]         = useState(() => {
    const d = new Date(); const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  });
  const [notes, setNotes]                   = useState("");
  const [confirmOverpay, setConfirmOverpay] = useState(false);
  const [busy, setBusy]                     = useState(false);

  const parsedAmount = Number(amountReceived);
  const isValidAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const variance = parsedAmount === expected ? "exact" : (parsedAmount < expected ? "partial" : "different");
  const isOverpay = parsedAmount > expected;
  const isUnderpay = parsedAmount < expected;
  const requiresReference = (method === "cashapp" || method === "card" || method === "ebay");
  const referenceOk = !requiresReference || referenceId.trim().length > 0
                       || method !== "zelle";  // reference encouraged, not blocking
  const overpayOk = !isOverpay || confirmOverpay;
  const canProceed = isValidAmount && referenceOk && !busy;
  const canSubmit  = canProceed && overpayOk;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        amount_received:   Number(parsedAmount.toFixed(2)),
        amount_variance:   variance,
        payment_method_actual: method,
        received_at:       new Date(receivedAt).toISOString(),
        internal_notes:    notes.trim() || null,
        confirm_overpayment: isOverpay ? true : false,
      };
      if (method === "zelle") {
        payload.zelle_reference   = referenceId.trim() || null;
        payload.zelle_sender_name = zelleSender.trim() || null;
      } else {
        payload.reference_id = referenceId.trim() || null;
      }
      await onConfirm(payload);
    } finally { setBusy(false); }
  };

  const methodOptions = [
    { v: "cash",    label: "Cash" },
    { v: "card",    label: "Card — In Store" },
    { v: "cashapp", label: "Cash App" },
    { v: "zelle",   label: "Zelle" },
    { v: "ebay",    label: "eBay" },
    { v: "other",   label: "Other" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-auto"
          role="dialog" aria-modal="true" data-testid="payment-confirmation-modal">
      <div className="bg-white border-4 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--panini-red)] max-w-lg w-full my-4">
        <div className="px-5 py-4 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                {step === 1 ? "Record / verify payment" : "Confirm before saving"}
              </div>
              <div className="font-display text-2xl">
                {step === 1 ? "Record this payment" : "Please confirm"}
              </div>
            </div>
            <button onClick={onCancel} className="btn-ghost text-sm" data-testid="payment-modal-close"><X size={14} /></button>
          </div>
        </div>

        {step === 1 && (
          <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3 text-sm" data-testid="payment-modal-summary">
              <PmmSummaryRow label="Customer"    value={order?.customer?.name || "—"} testid="pmm-customer" />
              <PmmSummaryRow label="Order #"     value={order?.public_id || "—"}      testid="pmm-order" />
              <PmmSummaryRow label="Order total" value={`$${expected.toFixed(2)}`}    testid="pmm-order-total" />
              <PmmSummaryRow label="Balance due" value={`$${balanceDueRemaining.toFixed(2)}`} testid="pmm-balance-due" />
            </div>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Payment method</span>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {methodOptions.map((opt) => (
                  <button key={opt.v} type="button" onClick={() => setMethod(opt.v)}
                          data-testid={`pmm-method-${opt.v}`}
                          className={`px-2 py-2 rounded-lg border-2 text-xs font-semibold ${method === opt.v
                            ? "border-[var(--panini-red)] bg-[var(--panini-red)] text-white"
                            : "border-[var(--ink)]/30 hover:border-[var(--ink)] bg-white"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {originalMethod && originalMethod !== method && (
                <div className="mt-1 text-xs text-[var(--smoke)]" data-testid="pmm-original-note">
                  Original selection: <span className="font-mono">{originalMethod}</span>
                </div>
              )}
            </label>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Amount received (USD)</span>
              <input type="number" step="0.01" min="0" value={amountReceived}
                      onChange={(e) => setAmountReceived(e.target.value)} autoFocus
                      className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] text-lg font-mono focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
                      data-testid="pmm-amount" />
              {isUnderpay && (
                <div className="mt-1 text-xs text-amber-700" data-testid="pmm-partial-hint">
                  Partial payment — order will stay in awaiting_payment. Remaining balance ${(expected - parsedAmount).toFixed(2)}.
                </div>
              )}
              {isOverpay && (
                <div className="mt-1 text-xs text-red-700 font-semibold" data-testid="pmm-overpay-hint">
                  Overpayment ${(parsedAmount - expected).toFixed(2)}. You&apos;ll be asked to confirm.
                </div>
              )}
            </label>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Received (date &amp; time)</span>
              <input type="datetime-local" value={receivedAt}
                      onChange={(e) => setReceivedAt(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] font-mono focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
                      data-testid="pmm-received-at" />
            </label>

            {method !== "cash" && (
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">
                  Reference / transaction ID {requiresReference ? <span className="text-[var(--smoke)]">(optional but recommended)</span> : "(optional)"}
                </span>
                <input type="text" value={referenceId}
                        onChange={(e) => setReferenceId(e.target.value)}
                        placeholder={method === "zelle" ? "Zelle transaction id"
                                    : method === "cashapp" ? "$cashtag note"
                                    : method === "card" ? "POS receipt / auth code"
                                    : method === "ebay" ? "eBay item / txn id"
                                    : "Reference"}
                        className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
                        data-testid="pmm-reference" />
              </label>
            )}

            {method === "zelle" && (
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Zelle sender name (optional)</span>
                <input type="text" value={zelleSender}
                        onChange={(e) => setZelleSender(e.target.value)}
                        placeholder="e.g. John Doe"
                        className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
                        data-testid="pmm-zelle-sender" />
              </label>
            )}

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Internal notes (optional)</span>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                         placeholder="Anything the team should know about this payment"
                         className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
                         data-testid="pmm-notes" />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto" data-testid="pmm-confirm-step">
            <div className="rounded-lg border-2 border-[var(--ink)] bg-[var(--cream)] p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[var(--smoke)]">Order #</span><span className="font-mono">{order?.public_id}</span></div>
              <div className="flex justify-between"><span className="text-[var(--smoke)]">Method</span><span className="font-semibold">{method}</span></div>
              <div className="flex justify-between"><span className="text-[var(--smoke)]">Amount</span><span className="font-mono">${parsedAmount.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--smoke)]">Order total</span><span className="font-mono">${expected.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--smoke)]">Received</span><span className="font-mono">{receivedAt}</span></div>
              {referenceId && <div className="flex justify-between"><span className="text-[var(--smoke)]">Reference</span><span className="font-mono truncate max-w-[60%]">{referenceId}</span></div>}
              {isUnderpay && (
                <div className="mt-2 text-xs text-amber-700 font-semibold">
                  Partial payment — order stays in <span className="font-mono">awaiting_payment</span>. Balance: ${(expected - parsedAmount).toFixed(2)}.
                </div>
              )}
              {isOverpay && (
                <div className="mt-2 text-xs text-red-700 font-semibold" data-testid="pmm-overpay-warning">
                  ⚠ Overpayment ${(parsedAmount - expected).toFixed(2)}. Order will still transition to payment_verified; the extra amount is flagged for follow-up.
                </div>
              )}
              {!isOverpay && !isUnderpay && (
                <div className="mt-2 text-xs text-emerald-700 font-semibold">
                  Full payment — order will move to <span className="font-mono">payment_verified</span> and appear under <span className="font-mono">Ready to Prepare</span>.
                </div>
              )}
            </div>
            {isOverpay && (
              <label className="flex items-center gap-2 text-sm" data-testid="pmm-overpay-checkbox-wrap">
                <input type="checkbox" checked={confirmOverpay}
                        onChange={(e) => setConfirmOverpay(e.target.checked)}
                        data-testid="pmm-overpay-checkbox" />
                <span>I acknowledge the overpayment and want to record it as shown.</span>
              </label>
            )}
          </div>
        )}

        <div className="px-5 py-4 border-t-2 border-[var(--ink)] flex items-center justify-end gap-2 bg-[var(--cream)]">
          {step === 1 && (
            <>
              <button onClick={onCancel} disabled={busy} className="btn-ghost text-sm" data-testid="pmm-cancel">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!canProceed} className="btn-red text-sm" data-testid="pmm-next">
                Next
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} disabled={busy} className="btn-ghost text-sm" data-testid="pmm-back">Back</button>
              <button onClick={submit} disabled={!canSubmit} className="btn-red text-sm" data-testid="pmm-confirm">
                {busy
                  ? <><Loader2 size={14} className="animate-spin" /> Recording…</>
                  : <><PackageCheck size={14} /> Record payment</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PmmSummaryRow({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border-2 border-[var(--ink)]/20 bg-[var(--cream)] px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className="mt-0.5 text-sm font-mono truncate">{value}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Phase 3A.1 · Save Progress → "Notify customer?" prompt
// ─────────────────────────────────────────────────────────────
function SaveProgressPromptModal({ count, onClose, onPreview }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          role="dialog" aria-modal="true" data-testid="save-prompt-modal">
      <div className="bg-white border-4 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--panini-yellow)] max-w-md w-full">
        <div className="px-5 py-4 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">Fulfillment progress saved</div>
          <div className="font-display text-2xl mt-0.5">Notify the customer?</div>
        </div>
        <div className="px-5 py-5 text-sm space-y-2" data-testid="save-prompt-body">
          <p>
            Your progress was saved successfully{count ? ` (${count} sticker${count === 1 ? "" : "s"} updated)` : ""}.
            Would you like to send the customer a fulfillment update email now?
          </p>
          <p className="text-xs text-[var(--smoke)]">
            You&apos;ll see the exact email preview before it&apos;s sent — nothing goes out without your confirmation.
          </p>
        </div>
        <div className="px-5 py-4 border-t-2 border-[var(--ink)] flex items-center justify-end gap-2 bg-[var(--cream)]">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="save-prompt-not-now">Not now</button>
          <button onClick={onPreview} className="btn-red text-sm" data-testid="save-prompt-preview">
            <Mail size={14} /> Preview customer email
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Fulfillment method badge (PICKUP / SHIPPING / DELIVERY REVIEW NEEDED)
// Row Badge Alignment — keyed off the shared backend classifier
// (`fulfillment_group`) so the badge always agrees with the Ready-
// bucket cards. Falls back to the legacy `fulfillment_method` value
// only when a projection predates the classifier field.
// ─────────────────────────────────────────────────────────────
function FulfillmentMethodBadge({ group, method, size = "md" }) {
  // The classifier-driven `group` is authoritative when the projection
  // supplies it. Only fall back to the legacy `method` field on older
  // payloads that predate the `fulfillment_group` field.
  const g = String(group || "").toLowerCase();
  const m = String(method || "").toLowerCase();
  let resolved;
  if (g === "pickup" || g === "ship" || g === "unknown") {
    resolved = g;
  } else if (m === "pickup") {
    resolved = "pickup";
  } else if (m === "shipping") {
    resolved = "ship";
  } else {
    resolved = "unknown";
  }
  const config = resolved === "pickup"
    ? { label: "PICKUP",   Icon: MapPin,        tone: "bg-emerald-100 text-emerald-900 border-emerald-700" }
    : resolved === "ship"
    ? { label: "SHIPPING", Icon: Truck,         tone: "bg-blue-100 text-blue-900 border-blue-700" }
    : { label: "DELIVERY REVIEW NEEDED", Icon: AlertTriangle, tone: "bg-red-100 text-red-900 border-red-700" };
  const cls = size === "sm"
    ? "text-[10px] px-2 py-0.5"
    : "text-xs px-2.5 py-1";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border-2 font-bold uppercase tracking-widest ${cls} ${config.tone}`}
           data-testid={`fulfillment-method-badge-${resolved}`}>
      <config.Icon size={size === "sm" ? 10 : 12} />
      {config.label}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────
// Manual Order Entry — source badge + WhatsApp deep-link button
// ─────────────────────────────────────────────────────────────
const MANUAL_SOURCE_LABELS = {
  instagram:  "Instagram",
  facebook:   "Facebook",
  whatsapp:   "WhatsApp",
  phone:      "Phone",
  in_person:  "In-Person",
  event:      "Event",
  other:      "Other",
};

function ManualSourceBadge({ source, size = "sm", testid }) {
  const label = MANUAL_SOURCE_LABELS[source] || "Manual";
  const cls = size === "sm"
    ? "text-[10px] px-2 py-0.5"
    : "text-xs px-2.5 py-1";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border-2 font-bold uppercase tracking-widest bg-yellow-100 text-yellow-900 border-yellow-700 ${cls}`}
      data-testid={testid || "manual-source-badge"}
      title={`Manually entered order · Source: ${label}`}
    >
      <Plus size={size === "sm" ? 10 : 12} />
      MANUAL · {label}
    </span>
  );
}

/**
 * WhatsApp deep-link button. Rendered only when the backend surfaces a
 * `whatsapp` envelope on the order (i.e. manual order + safely
 * normalized phone). The prefilled message is fully backend-controlled
 * so no internal notes, payment variance, or admin-only data ever
 * leaks through the UI.
 */
function WhatsAppButton({ order }) {
  const w = order.whatsapp;
  if (!w || !w.href) return null;
  return (
    <a
      href={w.href}
      target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-lg border-2 border-emerald-700 bg-emerald-600 text-white px-3 py-2 text-sm font-bold hover:bg-emerald-700"
      data-testid="whatsapp-message-button"
      title="Opens WhatsApp with a prefilled message you can edit before sending."
    >
      <MessageCircle size={16} /> Message on WhatsApp
    </a>
  );
}


// Instagram + TikTok handles surfaced in the order-detail header.
// TikTok has two possible sources — order-level override (per-order)
// vs the customer record's persistent value. When they differ we show
// both. Admin-only view — this row is never rendered on public
// trackers, customer emails, certificates, or public profiles.
function CustomerSocialsRow({ order }) {
  const customer = order.customer || {};
  const igRaw = (customer.instagram_username || customer.instagram_handle || "").trim();
  const tiktokCustomer = (customer.tiktok_username || "").trim();
  const tiktokOrder = (order.tiktok_username || "").trim();
  if (!igRaw && !tiktokCustomer && !tiktokOrder) return null;
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--smoke)]"
      data-testid="workspace-customer-socials"
    >
      {igRaw && (
        <span data-testid="workspace-customer-instagram">
          <span className="font-semibold text-[var(--ink)]">Instagram:</span>{" "}
          <span className="font-mono">@{igRaw.replace(/^@/, "")}</span>
        </span>
      )}
      {tiktokOrder && (
        <span data-testid="workspace-customer-tiktok-order">
          <span className="font-semibold text-[var(--ink)]">TikTok (this order):</span>{" "}
          <span className="font-mono">@{tiktokOrder.replace(/^@/, "")}</span>
        </span>
      )}
      {tiktokCustomer && tiktokCustomer !== tiktokOrder && (
        <span data-testid="workspace-customer-tiktok">
          <span className="font-semibold text-[var(--ink)]">TikTok (customer):</span>{" "}
          <span className="font-mono">@{tiktokCustomer.replace(/^@/, "")}</span>
        </span>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Phase 3A.1 · Ship-to (shipping orders) / Pickup (pickup orders)
// ─────────────────────────────────────────────────────────────
function AddressBlock({ order, pickupSettings }) {
  const method = order.fulfillment_method || "not_set";
  const status = order.shipping_address_status || "not_required";
  const addr = ((order.customer || {}).shipping_address) || {};
  const [busy, setBusy] = useState(false);

  const copyAddr = async () => {
    const name = (order.customer || {}).name || "";
    const label = [
      name,
      addr.line1,
      addr.line2,
      [addr.city, addr.state].filter(Boolean).join(", ") + (addr.postal_code ? ` ${addr.postal_code}` : ""),
      addr.country,
    ].filter((line) => (line || "").trim()).join("\n");
    try {
      await navigator.clipboard.writeText(label);
      toast.success("Shipping label copied.");
    } catch { toast.error("Copy failed — please copy manually."); }
  };

  if (method === "pickup") {
    return (
      <div className="mt-3 rounded-xl border-2 border-emerald-700 bg-emerald-50 p-4" data-testid="pickup-location-block">
        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900 flex items-center gap-1.5">
          <MapPin size={12} /> Pickup location
        </div>
        {pickupSettings?.pickup_location_name || pickupSettings?.pickup_location_address ? (
          <div className="mt-2 text-sm text-emerald-950 leading-relaxed">
            {pickupSettings.pickup_location_name && <div className="font-bold">{pickupSettings.pickup_location_name}</div>}
            {pickupSettings.pickup_location_address && <div className="whitespace-pre-line">{pickupSettings.pickup_location_address}</div>}
            {pickupSettings.pickup_location_hours && <div className="text-xs mt-1 text-emerald-800/80">Hours: {pickupSettings.pickup_location_hours}</div>}
            {pickupSettings.pickup_location_instructions && <div className="text-xs mt-1 text-emerald-800/80">{pickupSettings.pickup_location_instructions}</div>}
          </div>
        ) : (
          <div className="mt-2 text-sm text-emerald-900/80">
            Pickup location not configured yet — set it in <a className="underline" href="/admin?tab=settings">Website Settings</a>.
          </div>
        )}
        <div className="mt-2 text-xs text-emerald-900/80">Customer will pick up this order.</div>
      </div>
    );
  }

  if (method === "shipping") {
    return (
      <div className="mt-3 rounded-xl border-2 border-blue-700 bg-blue-50 p-4" data-testid="ship-to-block">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-blue-900 flex items-center gap-1.5">
            <Truck size={12} /> Ship to
          </div>
          {status === "ok" && (
            <button onClick={copyAddr} disabled={busy} className="btn-ghost text-[10px]" data-testid="copy-address-btn">
              <Copy size={10} /> Copy address
            </button>
          )}
        </div>
        {status === "missing" && (
          <div className="mt-2 rounded-lg border-2 border-amber-700 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900"
                data-testid="ship-to-missing-warning">
            Shipping address not captured — request from customer before fulfillment.
          </div>
        )}
        {status === "incomplete" && (
          <div className="mt-2 rounded-lg border-2 border-red-700 bg-red-50 px-3 py-2 text-xs font-bold text-red-900"
                data-testid="ship-to-incomplete-warning">
            Shipping address incomplete — verify before fulfillment.
          </div>
        )}
        {(status === "ok" || status === "incomplete") && (
          <div className="mt-2 text-sm text-blue-950 leading-relaxed" data-testid="ship-to-address">
            <div className="font-bold">{(order.customer || {}).name || "—"}</div>
            <div>{addr.line1 || <em className="text-red-800">Missing line 1</em>}</div>
            {addr.line2 && <div>{addr.line2}</div>}
            <div>
              {addr.city || <em className="text-red-800">Missing city</em>}
              {(addr.city || addr.state) ? ", " : ""}
              {addr.state || <em className="text-red-800">Missing state</em>}{" "}
              {addr.postal_code || <em className="text-red-800">Missing ZIP</em>}
            </div>
            <div>{addr.country || "United States"}</div>
          </div>
        )}
      </div>
    );
  }
  // not_set
  return (
    <div className="mt-3 rounded-xl border-2 border-amber-700 bg-amber-50 p-3 text-xs font-bold text-amber-900" data-testid="fulfillment-not-set">
      Fulfillment method not set — determine pickup or shipping before fulfillment.
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Phase 2 · Notify Customer (fulfillment update email)
// ─────────────────────────────────────────────────────────────
function NotifyCustomerModal({ publicId, onClose, onSent }) {
  const [state, setState] = useState({ loading: true, preview: null, error: null });
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    api.post(`/admin/orders/${publicId}/fulfillment-update-email/preview`, {})
      .then((r) => {
        if (!alive) return;
        setState({ loading: false, preview: r.data, error: null });
        setSubject(r.data.subject || "");
        setBody(r.data.body_text || "");
      })
      .catch((e) => {
        if (!alive) return;
        setState({ loading: false, preview: null,
                    error: formatApiError(e?.response?.data?.detail) || "Could not load preview." });
      });
    return () => { alive = false; };
  }, [publicId]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required."); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/orders/${publicId}/fulfillment-update-email/send`,
        { subject: subject.trim(), body_text: body.trim() });
      toast.success(r.data?.sent
        ? `Fulfillment update sent to ${r.data.recipient}.`
        : "Update logged — delivery couldn't be confirmed. Check email settings.");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Send failed.");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-auto"
          role="dialog" aria-modal="true" data-testid="notify-modal">
      <div className="bg-white border-4 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--panini-yellow)] max-w-2xl w-full my-4">
        <div className="px-5 py-4 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">Notify customer</div>
            <div className="font-display text-2xl">Fulfillment update email</div>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="notify-close"><X size={14} /></button>
        </div>

        {state.loading && (
          <div className="p-8 text-center text-sm text-[var(--smoke)]" data-testid="notify-loading">
            <Loader2 className="inline animate-spin mr-2" size={14} /> Building preview…
          </div>
        )}
        {state.error && (
          <div className="p-6 text-sm text-red-700" data-testid="notify-error">{state.error}</div>
        )}
        {state.preview && (
          <>
            <div className="px-5 py-4 border-b border-[var(--ink)]/20 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <NotifyStat testid="npv-ordered" label="Ordered" value={state.preview.ordered_count} />
              <NotifyStat testid="npv-picked"  label="Picked"  value={state.preview.picked_count} />
              <NotifyStat testid="npv-pending" label="Pending" value={state.preview.pending_count} />
              <NotifyStat testid="npv-percent" label="Complete" value={`${state.preview.percent_complete}%`} />
            </div>
            <div className="px-5 py-4 space-y-3 max-h-[55vh] overflow-y-auto">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Recipient</span>
                <div className="mt-1 font-mono text-sm" data-testid="notify-recipient">
                  {state.preview.customer_email || <em className="text-red-700">No email on file</em>}
                </div>
              </div>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Subject</span>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] text-sm"
                        data-testid="notify-subject" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">Body</span>
                <textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)}
                           className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] font-mono text-xs leading-relaxed"
                           data-testid="notify-body" />
              </label>
            </div>
            <div className="px-5 py-4 border-t-2 border-[var(--ink)] flex items-center justify-end gap-2 bg-[var(--cream)]">
              <button onClick={onClose} disabled={sending} className="btn-ghost text-sm" data-testid="notify-cancel">Cancel</button>
              <button onClick={send} disabled={sending || !state.preview.customer_email}
                       className="btn-red text-sm" data-testid="notify-send">
                {sending
                  ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
                  : <><Send size={14} /> Send email</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NotifyStat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border-2 border-[var(--ink)]/20 bg-white px-3 py-2 text-center">
      <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className="mt-0.5 font-display text-lg">{value}</div>
    </div>
  );
}




// ─────────────────────────────────────────────────────────────────
// Admin Ops · Cancel Order Modal
// Applies to unpaid orders (awaiting_payment / payment_submitted)
// AND legitimate pre-terminal cancels (payment_verified / preparing /
// ready). Preserves the order record + audit history; does NOT create
// a refund and does NOT mark payment verified. Backend enforces the
// legal transitions via the state machine.
// ─────────────────────────────────────────────────────────────────
const CANCEL_REASONS = [
  { value: "payment_never_received",  label: "Payment never received" },
  { value: "customer_stopped_responding", label: "Customer stopped responding" },
  { value: "customer_requested_cancel", label: "Customer requested cancellation" },
  { value: "duplicate_order",         label: "Duplicate order" },
  { value: "other",                   label: "Other" },
];

function CancelOrderModal({ order, busy, onClose, onConfirm }) {
  const [reasonKey, setReasonKey] = useState("payment_never_received");
  const [otherText, setOtherText] = useState("");
  const label = CANCEL_REASONS.find((r) => r.value === reasonKey)?.label || "";
  const composedReason = reasonKey === "other" ? otherText.trim() : label;
  const canConfirm = !busy && composedReason.length >= 4;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" data-testid="cancel-order-modal">
      <div className="w-full max-w-md bg-white border-2 border-[var(--ink)] rounded-2xl p-6 shadow-[8px_8px_0_var(--ink)]">
        <h3 className="font-display text-xl" data-testid="cancel-order-title">Cancel order {order.public_id}?</h3>
        <p className="text-sm text-[var(--smoke)] mt-2" data-testid="cancel-order-blurb">
          No payment was received for this order. Canceling will remove it from active payment/order queues while preserving the order record and history.
        </p>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[var(--smoke)]">Reason</label>
        <select
          className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm bg-white"
          value={reasonKey}
          onChange={(e) => setReasonKey(e.target.value)}
          data-testid="cancel-order-reason-select"
        >
          {CANCEL_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        {reasonKey === "other" && (
          <textarea
            className="w-full mt-3 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
            rows={3}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Describe the reason (≥ 4 characters)"
            data-testid="cancel-order-other-text"
          />
        )}
        <div className="mt-4 text-xs text-[var(--smoke)]" data-testid="cancel-order-effects">
          <div>• Order record + history preserved.</div>
          <div>• Removed from Open, Payment Review, Overdue and Missing Info queues.</div>
          <div>• Payment is <strong>not</strong> marked verified. No refund created.</div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button className="btn-ghost text-sm" onClick={onClose} data-testid="cancel-order-close">
            Nevermind
          </button>
          <button
            className="btn-red text-sm"
            disabled={!canConfirm}
            data-testid="cancel-order-confirm"
            onClick={() => onConfirm(composedReason)}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Confirm cancel
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Phase 3A.2 · Payment Reminder Modal (awaiting_payment orders)
// ─────────────────────────────────────────────────────────────────
function PaymentReminderModal({ publicId, onClose, onSent }) {
  const [state, setState] = useState({ loading: true, error: null, preview: null });
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, h] = await Promise.all([
          api.post(`/admin/orders/${publicId}/payment-reminder/preview`, {}),
          api.get(`/admin/orders/${publicId}/payment-reminders`),
        ]);
        if (cancelled) return;
        setSubject(p.data.subject || "");
        setBodyText(p.data.body_text || "");
        setState({ loading: false, error: null, preview: p.data });
        setHistory(h.data.items || []);
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          error: formatApiError(e?.response?.data?.detail) || "Preview failed.",
          preview: null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [publicId]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${publicId}/payment-reminder/send`, {
        subject, body_text: bodyText, override_cooldown: override,
      });
      toast.success("Payment reminder sent.");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Send failed.");
    } finally { setBusy(false); }
  };

  const cooldown = state.preview?.eligibility?.cooldown_remaining_seconds || 0;
  const canSend  = !!state.preview?.eligibility?.can_send;
  const reason   = state.preview?.eligibility?.reason || "";
  const hrs = Math.floor(cooldown / 3600);
  const mins = Math.floor((cooldown % 3600) / 60);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" role="dialog"
         data-testid="payment-reminder-modal">
      <div className="bg-white border-4 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--panini-yellow)] max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">Waiting payment · Order {publicId}</div>
            <div className="font-display text-2xl">Send payment reminder</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-white/70" data-testid="payment-reminder-close">
            <X size={16} />
          </button>
        </div>

        {state.loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div>
        ) : state.error || !state.preview ? (
          <div className="p-6 text-sm text-red-800" data-testid="payment-reminder-error">{state.error || "Unable to load preview."}</div>
        ) : (
          <div className="overflow-y-auto">
            <div className="grid md:grid-cols-3 gap-4 p-6">
              <div className="rounded-xl border-2 border-[var(--ink)]/15 bg-[var(--cream)] p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Customer</div>
                <div className="mt-1 font-bold" data-testid="reminder-customer-name">{state.preview.customer_name || "—"}</div>
                <div className="text-xs break-all" data-testid="reminder-customer-email">{state.preview.customer_email || "—"}</div>
              </div>
              <div className="rounded-xl border-2 border-[var(--ink)]/15 bg-[var(--cream)] p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Order total</div>
                <div className="mt-1 font-display text-2xl" data-testid="reminder-order-total">
                  {state.preview.order_total ? `$${Number(state.preview.order_total).toFixed(2)}` : "—"}
                </div>
                <div className="text-[10px] text-[var(--smoke)] uppercase tracking-widest mt-1">Status: {state.preview.order_status}</div>
              </div>
              <div className="rounded-xl border-2 border-[var(--ink)]/15 bg-[var(--cream)] p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Reminder history</div>
                <div className="mt-1 font-display text-2xl" data-testid="reminder-sent-count">{state.preview.reminders_sent}</div>
                <div className="text-[10px] text-[var(--smoke)] uppercase tracking-widest mt-1">
                  {state.preview.last_reminder_at
                    ? `Last: ${new Date(state.preview.last_reminder_at).toLocaleString()}`
                    : "No reminders yet"}
                </div>
              </div>
            </div>

            {!canSend && (
              <div className="mx-6 mb-4 rounded-lg border-2 border-red-700 bg-red-50 px-3 py-2 text-xs font-bold text-red-900"
                   data-testid="reminder-blocker">
                {reason || "Reminder cannot be sent right now."}
              </div>
            )}

            {canSend && cooldown > 0 && (
              <div className="mx-6 mb-4 rounded-lg border-2 border-amber-700 bg-amber-50 px-3 py-3 text-xs text-amber-900"
                   data-testid="reminder-cooldown">
                <div className="font-bold">
                  A payment reminder was sent recently — cooldown ends in {hrs}h {mins}m.
                </div>
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
                          data-testid="reminder-override" />
                  Send anyway (admin override — logged).
                </label>
              </div>
            )}

            <div className="px-6 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold">Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                        maxLength={200}
                        className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
                        data-testid="reminder-subject" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold">Message body (plain text)</span>
                <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)}
                          rows={12} maxLength={8000}
                          className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm font-mono leading-relaxed"
                          data-testid="reminder-body" />
              </label>
              <div className="text-[11px] text-[var(--smoke)]">
                A <span className="font-bold">COMPLETE MY ORDER</span> button, Zelle payment instructions,
                WhatsApp and Instagram links are appended automatically from Website Settings.
                {state.preview.tracker_url && (
                  <> Tracker URL: <a href={state.preview.tracker_url} target="_blank" rel="noreferrer" className="underline">{state.preview.tracker_url}</a></>
                )}
              </div>
            </div>

            {history.length > 0 && (
              <div className="px-6 pt-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-1">Recent reminders</div>
                <div className="rounded-lg border-2 border-[var(--ink)]/15 divide-y" data-testid="reminder-history-list">
                  {history.slice(0, 5).map((h) => (
                    <div key={h.id} className="px-3 py-2 flex items-center justify-between text-xs">
                      <div>
                        <span className="font-mono">#{h.reminder_sequence}</span>{" "}
                        {h.sent ? <span className="text-emerald-700">sent</span> : <span className="text-red-700">failed</span>}
                        {h.override_cooldown_used && <span className="ml-2 text-amber-700 font-bold">override</span>}
                      </div>
                      <div className="text-[var(--smoke)]">
                        {h.sent_at ? new Date(h.sent_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-4 border-t-2 border-[var(--ink)] bg-[var(--cream)] flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-ghost text-sm" data-testid="reminder-cancel">Cancel</button>
          <button onClick={send} disabled={busy || !canSend || (cooldown > 0 && !override)}
                  className="btn-red text-sm flex items-center gap-1" data-testid="reminder-send">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send reminder
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Availability follow-up email — Msg-937 promise fulfillment.
// Uses the order's fulfillment_items via the backend preview
// endpoint; NEVER computes availability client-side.
// ─────────────────────────────────────────────────────────────
function AvailabilityEmailCard({ order, onDone }) {
  const publicId = order.public_id;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [force, setForce] = useState(false);

  const last = order.availability_email;
  const hasPrevSend = last && last.status === "sent";

  async function openPreview() {
    setLoading(true); setError(""); setForce(false);
    try {
      const r = await api.get(`/admin/orders/${publicId}/availability-email/preview`);
      setPreview(r.data);
      setPreviewOpen(true);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message || "Preview failed.");
    } finally { setLoading(false); }
  }

  async function send() {
    if (!preview?.eligible) return;
    setSending(true);
    try {
      await api.post(`/admin/orders/${publicId}/availability-email/send${force ? "?force=true" : ""}`, { force });
      toast.success("Availability email sent.");
      setPreviewOpen(false);
      onDone?.();
    } catch (e) {
      const detail = formatApiError(e?.response?.data?.detail) || e.message || "Send failed.";
      // Duplicate-guard nudge: surface the "force" affordance inline.
      if (String(detail).toLowerCase().includes("already sent")) {
        setError(detail + " Toggle 'Send anyway' below to override.");
      } else {
        toast.error(detail);
      }
    } finally { setSending(false); }
  }

  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5" data-testid="availability-email-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="kicker kicker-red">Availability email</div>
          <h3 className="font-display text-xl mt-1">Customer availability update</h3>
          <p className="text-sm text-[var(--smoke)] mt-1 max-w-xl">
            Sends the customer a plain summary of which stickers are ready
            and which are still being sourced. Uses the current fulfillment
            statuses only — no admin notes or pricing details.
          </p>
        </div>
        <button onClick={openPreview} disabled={loading} className="btn-red text-sm" data-testid="availability-email-open-preview">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <Mail size={14}/>}
          {" "}{hasPrevSend ? "Resend availability update" : "Send availability update"}
        </button>
      </div>

      {last && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <MiniStat label="Last sent"     value={last.sent_at ? new Date(last.sent_at).toLocaleString() : "—"} testid="availability-last-sent"/>
          <MiniStat label="Ready count"   value={last.ready_count ?? 0}    testid="availability-last-ready"/>
          <MiniStat label="Pending count" value={last.pending_count ?? 0}  testid="availability-last-pending"/>
          <MiniStat label="Delivery"      value={last.status || "—"}       testid="availability-last-status" tone={last.status === "sent" ? "green" : "amber"}/>
        </div>
      )}

      {error && !previewOpen && (
        <div className="mt-3 text-xs text-red-800 bg-red-50 border-2 border-red-200 rounded-lg p-3" data-testid="availability-email-error">
          {error}
        </div>
      )}

      {previewOpen && preview && (
        <div className="mt-5 border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)] p-4" data-testid="availability-email-preview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <MiniStat label="Recipient"     value={preview.recipient_email || "—"} testid="preview-recipient"/>
            <MiniStat label="Ready"         value={preview.ready_count}   testid="preview-ready"/>
            <MiniStat label="Pending"       value={preview.pending_count} testid="preview-pending"/>
            <MiniStat label="Tracker link"  value={preview.tracker_url ? "included" : "omitted"} testid="preview-tracker"/>
          </div>
          <div className="mt-3 text-xs text-[var(--smoke)]"><b>Subject:</b> {preview.subject}</div>
          {!preview.eligible && (
            <div className="mt-3 text-xs text-red-900 bg-red-50 border-2 border-red-200 rounded-lg p-3" data-testid="availability-email-ineligible">
              {(preview.ineligible_reasons || []).join(" ")}
            </div>
          )}
          <div className="mt-4 border border-[var(--ink)] rounded-lg bg-white p-3 max-h-96 overflow-y-auto text-xs" data-testid="availability-email-html-preview">
            <iframe title="preview" srcDoc={preview.html} style={{ width: "100%", minHeight: 360, border: "0" }}/>
          </div>
          {hasPrevSend && (
            <label className="mt-3 inline-flex items-center gap-2 text-xs text-[var(--smoke)]" data-testid="availability-email-force-toggle">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}/>
              Send anyway (override duplicate-send guard)
            </label>
          )}
          {error && (
            <div className="mt-2 text-xs text-red-800 bg-red-50 border-2 border-red-200 rounded-lg p-2">{error}</div>
          )}
          <div className="mt-4 flex items-center gap-3 justify-end">
            <button onClick={() => { setPreviewOpen(false); setPreview(null); setError(""); }} className="btn-ghost text-xs" data-testid="availability-email-cancel">Cancel</button>
            <button onClick={send} disabled={sending || !preview.eligible} className="btn-red text-xs" data-testid="availability-email-confirm-send">
              {sending ? <><Loader2 size={12} className="animate-spin"/> Sending…</> : <><Send size={12}/> Confirm &amp; send</>}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function MiniStat({ label, value, testid, tone }) {
  const toneCls = tone === "green"
    ? "border-emerald-700 bg-emerald-50 text-emerald-900"
    : tone === "amber"
    ? "border-amber-700 bg-amber-50 text-amber-900"
    : "border-[var(--ink)] bg-white";
  return (
    <div className={`rounded-lg border-2 px-3 py-2 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">{label}</div>
      <div className="text-sm font-bold truncate">{value}</div>
    </div>
  );
}

